import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  getCountFromServer,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  JobV2,
  JobSpecimenLineItem,
  JobServiceLineItem,
  JobLineItem,
  JobFile,
  JobPaymentResult,
  JobPayment,
  JobClient,
} from "../@types/job";
import {
  Client,
  ClientFile,
  ClientJobBaseLineItem,
  ClientJobLineItem,
  ClientJobServiceLineItem,
  ClientJobSpecimenLineItem,
  ClientJobV2,
} from "../@types/client";

import { HistoricalChange } from "../@types/history";
import { NamedUserUID, PaymentType, SoaStages } from "../@types/enums";
import { isServiceLineItem, isSpecimenLineItem } from "../_helpers/lineItem";
import { mockJobData } from "../_constants/mockJobAndClient";
import {
  deleteEntireFolder,
  deleteFiles,
  uploadJobFiles,
  uploadLineItemFiles,
} from "./storage";
import dayjs from "dayjs";

import { getJobChanges } from "../_helpers/getJobChanges";
import {
  computeMaterialDelta,
  mountIdsByInventoryItem,
} from "../_helpers/materialDiff";
import { stageMaterialDelta } from "./inventory";
import { PaymentScheduleItem, JobContract, JobWrd } from "../@types/job";

import {
  calculateAddition,
  calculateDisplayGrandTotal,
  calculateDisplayRemainingBalance,
  calculateJob,
  toNearZero,
} from "../_helpers/math";
import { QueryClient } from "@tanstack/react-query";
import { User } from "../@types/user";
import { stripProcessingFeeServiceNameFromLineItems } from "../_helpers/processingFees";
import {
  assertLocationsHaveRoom,
  collectPartsEnteringLocations,
  createPartLocation,
  deletePartLocation,
  isLocationFullError,
  removeJobPartsFromLocations,
  shiftPartLocation,
  updatePartLocation,
} from "./parts";

import { getStages } from "./stages";
import { clientJobEntryUnchanged } from "../_helpers/clientJobEquality";
import { jobClientSwitchBlockedReason } from "../_helpers/jobClientSwitch";
import { authenticatedFetch } from "../_utils/authenticatedFetch";
import { readJson } from "../_utils/apiJson";
import { generateUid } from "./general";
import { QBBankAccount } from "../@types/quickbooks";
import { outOfBandPaymentReconciliation } from "../_helpers/job";
import { reconcileStaleJobPayments } from "../_helpers/reconcileStaleJobPayments";
import { reconcileStaleJobFiles } from "../_helpers/reconcileStaleJobFiles";
import { getTabId } from "../_helpers/tabId";
import { JobBasic } from "../@types/jobBasic";
import { getJobsIndex, isJobsIndexLive, jobsIndexQueryKey } from "./jobsIndex";
import {
  WRD_FILE_NAME,
  isWrdFile,
} from "../_helpers/collectionRequirements";
import { applyRecordNotesToLineItems } from "../_helpers/complianceDocument";
import {
  preflightQuickBooksJobUpdateSync,
  validateQuickBooksLineItemsForJob,
  throwIfQuickBooksApiError,
} from "../_helpers/quickbooksJobSync";
import { isQbToBillInvoiceJob } from "../_constants/tags";

export const getJobsV2 = async (uid: string): Promise<JobV2[]> => {
  try {
    const jobsCollectionRef = collection(db, "users", uid, "jobs");
    const queryConstraints = [orderBy("createdAt", "desc")];

    const dataQuery = query(jobsCollectionRef, ...queryConstraints);
    const querySnapshot = await getDocs(dataQuery);

    return querySnapshot.docs.map((doc) => doc.data() as JobV2);
  } catch (error) {
    throw error;
  }
};

// Every job that has, or had, a payment plan. Paying a plan off early
// flips hasPaymentPlan to false, so those jobs need their own query.
export const getPaymentPlanJobs = async (uid: string): Promise<JobV2[]> => {
  const jobsCollectionRef = collection(db, "users", uid, "jobs");
  const [active, finishedEarly] = await Promise.all([
    getDocs(
      query(jobsCollectionRef, where("contract.hasPaymentPlan", "==", true))
    ),
    getDocs(
      query(
        jobsCollectionRef,
        where("contract.finishedByOutOfBandPayment", "==", true)
      )
    ),
  ]);
  const byUid = new Map<string, JobV2>();
  for (const snap of [...active.docs, ...finishedEarly.docs]) {
    byUid.set(snap.id, snap.data() as JobV2);
  }
  return Array.from(byUid.values());
};

// Stamp a qbPaymentId onto one payment by uid, transactionally. The QB
// payment webhook can write to the job between our QB create call and this
// write-back, and a blind updateDoc({ payments }) from local state would
// clobber whatever it wrote. Re-reading inside a transaction touches only
// the target payment and leaves every other field alone.
export const stampQbPaymentIdOnJobPayment = async (
  userId: string,
  jobUid: string,
  paymentUid: string,
  qbPaymentId: string
): Promise<void> => {
  const jobRef = doc(db, `users/${userId}/jobs/${jobUid}`);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(jobRef);
    if (!snapshot.exists()) return;
    const payments: JobPayment[] = snapshot.data().payments || [];
    const target = payments.find((payment) => payment.uid === paymentUid);
    if (!target || target.qbPaymentId === qbPaymentId) return;
    transaction.update(jobRef, {
      payments: payments.map((payment) =>
        payment.uid === paymentUid ? { ...payment, qbPaymentId } : payment
      ),
      // This runs in the same tab that just took the payment, so claim the
      // write; otherwise that tab warns itself that payments changed.
      lastWriteTabId: getTabId(),
    });
  });
};

// Find the MountMonitor job (if any) that owns a given QuickBooks invoice.
// Used by the POS one-off flow to stop payments on MM-managed invoices —
// those must go through the job payment flow so both systems stay in sync.
// True once a payment carrying this Stripe PaymentIntent is saved on the
// job(s). Named jobs that exist must all carry it (a split lands on every
// job). A new job only gets its id when it is created, so when a named job
// does not exist yet, any job saved since the charge counts.
export const isPaymentIntentOnJobs = async (
  userId: string,
  paymentIntentId: string,
  jobUids: string[],
  since: Date
): Promise<boolean> => {
  const hasIntent = (job: any) =>
    (job?.payments ?? []).some(
      (payment: any) => payment?.stripePaymentIntentId === paymentIntentId
    );

  const named = jobUids.filter(Boolean);
  if (named.length > 0) {
    const snaps = await Promise.all(
      named.map((jobUid) => getDoc(doc(db, `users/${userId}/jobs/${jobUid}`)))
    );
    if (snaps.every((snap) => snap.exists())) {
      return snaps.every((snap) => hasIntent(snap.data()));
    }
  }

  const recent = await getDocs(
    query(
      collection(db, `users/${userId}/jobs`),
      where("updatedAt", ">=", Timestamp.fromDate(since))
    )
  );
  return recent.docs.some((snap) => hasIntent(snap.data()));
};

export const getJobByQbInvoiceId = async (
  userId: string,
  qbInvoiceId: string
): Promise<JobV2 | null> => {
  try {
    const jobsCollectionRef = collection(db, "users", userId, "jobs");
    const dataQuery = query(
      jobsCollectionRef,
      where("qbInvoiceId", "==", qbInvoiceId),
      limit(1)
    );
    const querySnapshot = await getDocs(dataQuery);
    if (querySnapshot.empty) return null;
    return querySnapshot.docs[0].data() as JobV2;
  } catch (error) {
    console.error("getJobByQbInvoiceId failed:", error);
    return null;
  }
};

export const getJobByJobId = async (
  userId: string,
  jobId: number
): Promise<JobV2> => {
  const jobsCollectionRef = query(
    collection(db, "users", userId, "jobs"),
    where("jobId", "==", jobId)
  );
  try {
    const querySnapshot = await getDocs(jobsCollectionRef);
    const jobs: JobV2[] = [];
    querySnapshot.forEach((doc) => {
      jobs.push(doc.data() as JobV2);
    });

    // TODO: Logging if more than one job, JobId more unique and should return 1 only
    return jobs[0];
  } catch (error) {
    throw error;
  }
};

export async function createJobV2(
  user: User,
  job: JobV2
): Promise<{
  newJob: JobV2;
  newClientJob: ClientJobV2;
  duplicateDocNumberRetried?: boolean;
  qbInvoiceAdopted?: boolean;
  qbInvoiceAdoptionFailed?: boolean;
}> {
  try {
    const {
      uid: userId,
      settings: { qbIncomeAccount, qbTaxGroup, qbBankAccount, stripeAccountId },
    } = user;

    const {
      client,
      shipping,
      priority,
      tags,
      notes,
      ids,
      files,
      lineItems,
      payments,
      paymentStatus,
      subtotal,
      totalDiscount,
      subtotalAfterDiscounts,
      taxRate,
      includeTax,
      totalTax,
      grandTotal,
      totalDeposit,
      remainingBalance,
      syncedToAccountingIntegration,
      contract,
      wrd,
    } = job;

    const jobsCollectionRef = collection(db, `users/${userId}/jobs`);
    const userMetaJobsDocRef = doc(db, `users/${userId}/metadata/jobs`);
    const clientDocRef = doc(db, `users/${userId}/clients/${client.uid}`);
    let clientDoc: Client;
    let stagedMaterial: Awaited<ReturnType<typeof stageMaterialDelta>> | null =
      null;

    if (
      syncedToAccountingIntegration &&
      qbIncomeAccount &&
      qbBankAccount &&
      qbTaxGroup
    ) {
      await validateQuickBooksLineItemsForJob(user, lineItems);
    }

    // Refuse a full location before anything is written, so the job never
    // claims parts its location did not accept.
    await assertLocationsHaveRoom(
      userId,
      collectPartsEnteringLocations([], lineItems)
    );

    const result = await runTransaction(db, async (transaction) => {
      const userMetadataDoc = await transaction.get(userMetaJobsDocRef);
      clientDoc = (await transaction.get(clientDocRef)).data() as Client;

      // A synced job needs a QuickBooks customer to invoice. The form derives
      // its sync flag from cached client state and can get it wrong for a
      // just-created client, so check the real client doc before any write.
      // Without this the job commits and the invoice call crashes, leaving a
      // synced job with no invoice that fails on every later save.
      if (
        syncedToAccountingIntegration &&
        !isQbToBillInvoiceJob(tags) &&
        qbIncomeAccount &&
        qbBankAccount &&
        qbTaxGroup &&
        !clientDoc?.quickbooks?.id
      ) {
        throw new Error(
          `${client.fullName} is not synced to QuickBooks, so this job can't be synced. Turn QuickBooks sync on for the client first, or turn it off for this job.`
        );
      }
      const currentCounter = userMetadataDoc?.data()?.jobNum || 0;

      // Use custom jobId if provided, otherwise auto-increment
      const newJobId = job.jobId || currentCounter + 1;

      // Check for duplicate jobId if custom number is provided
      if (job.jobId) {
        const existingJob = await getJobByJobId(userId, job.jobId);
        if (existingJob) {
          throw new Error(`Job #${job.jobId} already exists`);
        }
      }

      const newJobRef = doc(jobsCollectionRef);

      // This is to handle users putting their own creation date
      const createdAtTime =
        job.createdAt instanceof Date
          ? Timestamp.fromDate(dayjs.utc(job.createdAt as Date).toDate())
          : Timestamp.now();

      const updatedAtTime = Timestamp.now();

      const specimenLineItems = lineItems.filter(
        (lineItem): lineItem is JobSpecimenLineItem =>
          isSpecimenLineItem(lineItem)
      );

      const stages = await getStages(userId);

      let completedAtTime =
        specimenLineItems.length > 0 &&
        specimenLineItems.every((lineItem) => {
          const stage = stages.find((s) => s.name === lineItem.stage);
          return stage?.isCompletedStage || false;
        })
          ? updatedAtTime
          : null;

      if (completedAtTime) {
        job.tags = job.tags.filter((tag) => tag !== "active");
        job.tags.push("closed");
        job.tags = Array.from(new Set([...job.tags]));
      } else {
        job.tags = job.tags.filter((tag) => tag !== "closed");
        job.tags.push("active");
        job.tags = Array.from(new Set([...job.tags]));
      }

      const wrdFile = files.find((file) => isWrdFile(file.name));
      const filesWithoutWrd = files.filter((file) => !isWrdFile(file.name));

      const jobLevelFiles =
        filesWithoutWrd.length > 0
          ? await uploadJobFiles(
              filesWithoutWrd as File[],
              userId,
              newJobRef.id
            )
          : [];

      const jobFiles = [...(wrdFile ? [wrdFile] : []), ...jobLevelFiles];

      let stripeCustomerId = null;

      if (clientDoc.stripeCustomerId) {
        stripeCustomerId = clientDoc.stripeCustomerId;
      } else if (job.contract?.hasPaymentPlan) {
        try {
          const stripeCustomerIdResponse = await authenticatedFetch(
            "/api/stripe/create-customer",
            {
              method: "POST",
              body: JSON.stringify({
                clientData: {
                  firstName: clientDoc.firstName,
                  lastName: clientDoc.lastName,
                  email: clientDoc.email,
                  phone: clientDoc.phone,
                  address: clientDoc.address,
                },
                clientUid: clientDoc.uid,
                stripeAccountId: stripeAccountId,
              }),
            }
          );
          stripeCustomerId = await readJson(stripeCustomerIdResponse);
        } catch (error) {
          throw error;
        }
      }

      const newJob: JobV2 = {
        uid: newJobRef.id,
        jobId: newJobId,
        client: {
          uid: clientDoc.uid,
          firstName: clientDoc.firstName,
          lastName: clientDoc.lastName,
          fullName:
            `${clientDoc.firstName} ${clientDoc.lastName}`.toLowerCase(),
          contactDetails: {
            phone: clientDoc.phone,
            email: clientDoc.email,
          },
          ...(clientDoc.address
            ? { address: clientDoc.address }
            : client.address
            ? { address: client.address }
            : {}),
          currency: clientDoc.currency,
          syncedToAccountingIntegration:
            clientDoc.syncedToAccountingIntegration,
          autoStageEmails: clientDoc.autoStageEmails ?? null,
          autoStageSMS: clientDoc.autoStageSMS ?? null,
        },
        shipping,
        priority: (Number(priority) as Priority) || null,
        tags,
        notes,
        ids,
        files: jobFiles,
        lineItems: await Promise.all(
          lineItems.map(async (lineItem): Promise<JobLineItem> => {
            const {
              uid,
              tags,
              notes,
              files,
              createdAt,
              updatedAt,
              price,
              chargePerSquareUnit,
              discountedPrice,
              depositAmount,
              deposit,
              depositType,
              discountAmount,
              discount,
              discountType,
            } = lineItem;

            // Photos from the booking form arrive already uploaded; only
            // fresh picks need uploading.
            const newFiles = files.filter(
              (file): file is File => file instanceof File
            );
            const uploadedFiles = files.filter(
              (file) => !(file instanceof File)
            ) as JobFile[];

            const commonFields = {
              uid,
              tags,
              notes,
              files: [
                ...uploadedFiles,
                ...(newFiles.length > 0
                  ? await uploadLineItemFiles(
                      newFiles,
                      userId,
                      newJobRef.id,
                      uid
                    )
                  : []),
              ],
              price,
              chargePerSquareUnit,
              discountedPrice,
              depositAmount,
              deposit,
              depositType: depositAmount ? depositType : null,
              discountAmount,
              discount,
              discountType: discountAmount ? discountType : null,
              createdAt: createdAtTime,
              updatedAt: updatedAtTime,
            };

            if (isSpecimenLineItem(lineItem)) {
              const specimenLineItem: JobSpecimenLineItem = {
                ...commonFields,
                frontFacingId: lineItem.frontFacingId,
                quantity: lineItem.quantity,
                stage: lineItem.stage,
                habitat: lineItem.habitat,

                specimen: {
                  ...lineItem.specimen,
                  harvestDate: lineItem.specimen.harvestDate
                    ? lineItem.specimen.harvestDate instanceof Date
                      ? Timestamp.fromDate(
                          dayjs.utc(lineItem.specimen.harvestDate).toDate()
                        )
                      : lineItem.specimen.harvestDate
                    : null,
                  measurements: Object.values(
                    lineItem.specimen.measurements || {}
                  ).every((value) => value === null)
                    ? null
                    : Object.keys(lineItem.specimen.measurements!).reduce(
                        (acc, cur) => {
                          return {
                            ...acc,
                            [cur]: Number(lineItem.specimen.measurements![cur]),
                          };
                        },
                        {} as Record<string, number>
                      ),
                  parts: lineItem.specimen.parts.map((part) => ({
                    ...part,
                    jobUid: newJobRef.id,
                    lineItemUid: uid,
                    jobId: newJobId,
                    species: lineItem.specimen.species,
                    frontFacingId: lineItem.frontFacingId,
                    createdAt: createdAtTime,
                    updatedAt: updatedAtTime,
                  })),
                },
                estimatedCompletedAt: lineItem.estimatedCompletedAt
                  ? lineItem.estimatedCompletedAt instanceof Date
                    ? Timestamp.fromDate(
                        dayjs.utc(lineItem.estimatedCompletedAt).toDate()
                      )
                    : lineItem.estimatedCompletedAt
                  : null,
                completedAt: (() => {
                  const stage = stages.find((s) => s.name === lineItem.stage);
                  return stage?.isCompletedStage ? updatedAtTime : null;
                })(),
                ...(lineItem.materials?.length ? { materials: lineItem.materials } : {}),
              };
              return specimenLineItem;
            } else {
              const serviceLineItem: JobServiceLineItem = {
                ...commonFields,
                service: lineItem.service,
                linkedSpecimenUid: lineItem.linkedSpecimenUid || null,
                ticketNumber: lineItem.ticketNumber || null,
                ...(lineItem.materials?.length ? { materials: lineItem.materials } : {}),
              };
              return serviceLineItem;
            }
          })
        ),
        payments: payments.map((payment) => ({
          ...payment,
          date:
            payment.date instanceof Date
              ? Timestamp.fromDate(dayjs.utc(payment.date).toDate())
              : payment.date,
        })),
        paymentStatus,
        subtotal,
        totalDiscount,
        subtotalAfterDiscounts,
        syncedToAccountingIntegration,
        taxRate,
        includeTax,
        totalTax,
        grandTotal,
        totalDeposit,
        remainingBalance,
        wrd:
          wrd?.required || wrd?.isSigned
            ? {
                required: true,
                isSigned: wrd.isSigned || false,
                signedAt: wrd.signedAt || null,
                signedMethod: wrd.signedMethod || null,
                fileUrl: wrd.fileUrl || null,
                donor: wrd.donor || null,
                licenseType: wrd.licenseType || null,
                licenseNumber: wrd.licenseNumber || "",
                licenseState: wrd.licenseState || "",
                ranchOutfitter: wrd.ranchOutfitter || "",
                county: wrd.county || "",
                state: wrd.state || "",
                mosesId: wrd.mosesId || "",
              }
            : null,
        contract: contract
          ? (() => {
              const hasInitialPayment =
                contract.paymentSchedule?.[0]?.status !== "unprocessed";

              return {
                uid: generateUid(),
                jobUid: newJobRef.id,
                clientUid: client.uid,
                userUid: userId,

                contractUrl: contract.contractUrl || null,
                contractHtml: contract.contractHtml || null,

                isSigned: contract.isSigned || false,
                signatureRequired: contract.signatureRequired || false,
                signedAt: contract.signedAt || null,
                signedMethod: contract.signedMethod || null,

                hasPaymentPlan: contract.hasPaymentPlan || false,
                paymentSchedule: contract.paymentSchedule || null,

                // Denormalized fields for efficient Firestore querying
                nextPaymentDate: null,
                nextPaymentNumber: !contract.hasPaymentPlan
                  ? null
                  : !hasInitialPayment
                  ? 0
                  : 1,
                failedPayments: contract.failedPayments || null,
                // Computed/derived values
                totalPayments: contract.totalPayments || null,
                monthsBetweenPayments: contract.monthsBetweenPayments || null,
                // Error tracking

                // Stripe integration
                stripeCustomerId: stripeCustomerId || null,
                stripePaymentMethodId: clientDoc.stripePaymentMethodId || null,
                stripeConnectAccountId: stripeAccountId || null,

                createdAt: createdAtTime,
                updatedAt: updatedAtTime,
              } as JobContract;
            })()
          : null,
        staff: [],
        actions: [],
        staffQuery: [],
        actionsQuery: [],
        history: [],
        createdAt: createdAtTime,
        updatedAt: updatedAtTime,
        completedAt: completedAtTime,
      };

      const newClientJob: ClientJobV2 = {
        uid: newJobRef.id,
        jobId: newJobId,
        lineItems: newJob.lineItems.map((lineItem): ClientJobLineItem => {
          const commonFields: ClientJobBaseLineItem = {
            uid: lineItem.uid,
            discount: lineItem.discount,
            deposit: lineItem.deposit,
            price: lineItem.price,
            discountedPrice: lineItem.discountedPrice,
            createdAt: lineItem.createdAt as Timestamp,
            updatedAt: lineItem.updatedAt as Timestamp,
            files: lineItem.files as ClientFile[],
          };
          if (isSpecimenLineItem(lineItem)) {
            const specimenVersion: ClientJobSpecimenLineItem = {
              ...commonFields,
              quantity: lineItem.quantity,
              estimatedCompletedAt: lineItem.estimatedCompletedAt as Timestamp,
              completedAt: lineItem.completedAt as Timestamp,
              // These will always both be here at this stage
              specimen: lineItem.specimen.species!,
              mountType: lineItem.specimen.mountType!,
              stage: lineItem.stage as Stage,
            };
            return specimenVersion;
          } else {
            const serviceVersion: ClientJobServiceLineItem = {
              ...commonFields,
              service: lineItem.service,
            };
            return serviceVersion;
          }
        }),
        paymentStatus,
        totalDiscount,
        grandTotal,
        totalDeposit,
        remainingBalance,
        createdAt: newJob.createdAt as Timestamp,
        updatedAt: newJob.updatedAt as Timestamp,
        completedAt: newJob.completedAt,
      };

      const createMaterialDelta = computeMaterialDelta([], newJob.lineItems);
      if (createMaterialDelta.size > 0) {
        stagedMaterial = await stageMaterialDelta(
          transaction,
          userId,
          createMaterialDelta,
          userId,
          "Material used on job",
          user.details,
          {
            jobId: newJobId,
            jobUid: newJobRef.id,
            mountIds: mountIdsByInventoryItem(newJob.lineItems),
          }
        );
      }

      transaction.set(newJobRef, newJob);
      console.log("newClientJob", newClientJob);
      console.log("clientDocRef", clientDocRef);
      transaction.set(
        clientDocRef,
        {
          jobs: arrayUnion(newClientJob),
          ...(stripeCustomerId && { stripeCustomerId }),
        },
        {
          merge: true,
        }
      );
      clientDoc.jobs = [...clientDoc.jobs, newClientJob];

      // Only update counter if new jobId is higher than current counter
      if (newJobId > currentCounter) {
        transaction.set(
          userMetaJobsDocRef,
          { jobNum: newJobId },
          { merge: true }
        );
      }

      stagedMaterial?.apply();

      return { newJob, newClientJob };
    });

    // Write parts into the location cache AFTER the job is committed, awaited —
    // so an aborted transaction can't orphan entries and a retry can't
    // double-write. A failure here can't block the (already created) job; the
    // entry self-heals on the next move or a rebuild.
    try {
      await Promise.all(
        result.newJob.lineItems
          .filter(isSpecimenLineItem)
          .flatMap((lineItem) =>
            lineItem.specimen.parts.map((part) =>
              createPartLocation(userId, part)
            )
          )
      );
    } catch (e) {
      console.error(
        "createJobV2: part-cache write failed (job already created; self-heals on next move/rebuild):",
        e
      );
    }

    if (stagedMaterial) {
      await stagedMaterial.postCommit();
    }

    if (
      result.newJob.syncedToAccountingIntegration &&
      !isQbToBillInvoiceJob(result.newJob.tags) &&
      qbIncomeAccount &&
      qbBankAccount &&
      qbTaxGroup
    ) {
      const newJob = result.newJob;
      const newClientJob = result.newClientJob;
      const strippedLineItems = stripProcessingFeeServiceNameFromLineItems(
        newJob.lineItems
      );

      const checkItemsResponse = await authenticatedFetch(
        `/api/quickbooks/items/checkItems`,
        {
          method: "POST",
          body: JSON.stringify({
            lineItems: strippedLineItems,
            incomeAccountId: qbIncomeAccount.id,
          }),
        }
      );
      const qbItems = await checkItemsResponse.json();

      if (qbItems.error) {
        throwIfQuickBooksApiError(qbItems);
      }

      // If its not mesquite it creates the payment on the invoice as deposit or full payment in endpoint
      const newQbInvoiceResponse = await authenticatedFetch(
        `/api/quickbooks/job/create`,
        {
          method: "POST",
          body: JSON.stringify({
            userCountry: user.settings.address.country,
            job: newJob,
            client: clientDoc,
            qbItems: qbItems.data,
            qbTaxGroup: qbTaxGroup,
            qbBankAccount: qbBankAccount,
          }),
        }
      );

      const newQbInvoice = await newQbInvoiceResponse.json();

      if (newQbInvoice.error) {
        authenticatedFetch("/api/email/errorNotification", {
          method: "POST",
          body: JSON.stringify({
            userName: user.settings.name,
            userId: userId,
            error: `Failed to create QB invoice for ${newJob.uid}: ${newQbInvoice.error}`,
            location: `createJob - QB payment creation invoice`,
          }),
        });
        return result;
      }

      const duplicateDocNumberRetried =
        newQbInvoice.duplicateDocNumberRetried || false;

      newJob.qbInvoiceId = newQbInvoice.data.Id;
      newClientJob.qbInvoiceId = newQbInvoice.data.Id;

      // For mesquite we do not create payments deposit/full on invoice
      // we do separate payments
      let updatedPayments: JobPayment[] = [];
      if (userId === NamedUserUID.Mesquite && job.payments.length > 0) {
        const firstPayment = newJob.payments[0];

        const payment: {
          jobId: string;
          client: Client;
          jobPayment: JobPayment;
          qbInvoiceId: string;
          qbBankAccount: QBBankAccount;
        } = {
          jobId: newJob.jobId.toString(),
          client: clientDoc,
          jobPayment: firstPayment,
          qbInvoiceId: newJob.qbInvoiceId,
          qbBankAccount: qbBankAccount,
        };

        try {
          const response = await authenticatedFetch(
            "/api/quickbooks/payments/create",
            {
              method: "POST",
              body: JSON.stringify(payment),
            }
          );

          if (response.ok) {
            const newQbPaymentResponse = await response.json();
            const qbPaymentId = newQbPaymentResponse.data.Id;

            updatedPayments = [...newJob.payments];
            updatedPayments[0].qbPaymentId = qbPaymentId;
            newJob.payments = [...updatedPayments];

            await stampQbPaymentIdOnJobPayment(
              userId,
              newJob.uid,
              updatedPayments[0].uid,
              qbPaymentId
            );
          }
        } catch (error) {
          authenticatedFetch("/api/email/errorNotification", {
            method: "POST",
            body: JSON.stringify({
              userName: user.settings.name,
              userId: userId,
              error: `Failed to create QB payment for ${newJob.uid}: ${error}`,
              location: `createJob - QB payment creation invoice ${newQbInvoice.data.Id}`,
            }),
          });
          // we dont want to return here cause we wana atleast know the invoice
        }
      }

      const newJobRef = doc(db, `users/${userId}/jobs/${newJob.uid}`);
      const clientDocRef = doc(db, `users/${userId}/clients/${client.uid}`);
      console.log("clientDoc", clientDoc);
      await updateDoc(clientDocRef, {
        jobs: clientDoc.jobs.map((job) =>
          job.uid === newJob.uid
            ? { ...newClientJob, qbInvoiceId: newQbInvoice.data.Id }
            : job
        ),
      });

      await updateDoc(newJobRef, {
        qbInvoiceId: newQbInvoice.data.Id,
      });

      if (duplicateDocNumberRetried) {
        return { ...result, duplicateDocNumberRetried: true };
      }
    }

    // "QuickBooks to Bill Invoice" jobs: QB owns the invoice, so we never
    // create one. Instead, adopt the existing QB invoice whose DocNumber
    // matches the job number — store its internal Id (and hosted payment
    // link) on the job so the invoice drawer and portal can surface it.
    // Keyed off the tag alone (not the sync flag) so a tagged job can never
    // fall through to invoice creation regardless of the flag's state.
    if (isQbToBillInvoiceJob(result.newJob.tags)) {
      const newJob = result.newJob;
      const newClientJob = result.newClientJob;

      try {
        const adoptResponse = await authenticatedFetch(
          `/api/quickbooks/job/adopt?docNumber=${newJob.jobId}`
        );
        const adopted = await adoptResponse.json();

        if (adopted.data?.Id) {
          newJob.qbInvoiceId = adopted.data.Id;
          newJob.qbInvoiceLink = adopted.data.InvoiceLink || null;
          newClientJob.qbInvoiceId = adopted.data.Id;

          const newJobRef = doc(db, `users/${userId}/jobs/${newJob.uid}`);
          const clientDocRef = doc(
            db,
            `users/${userId}/clients/${client.uid}`
          );

          await updateDoc(clientDocRef, {
            jobs: clientDoc.jobs.map((clientJob) =>
              clientJob.uid === newJob.uid
                ? { ...newClientJob, qbInvoiceId: adopted.data.Id }
                : clientJob
            ),
          });

          await updateDoc(newJobRef, {
            qbInvoiceId: adopted.data.Id,
            qbInvoiceLink: adopted.data.InvoiceLink || null,
          });

          return { ...result, qbInvoiceAdopted: true };
        }

        // No QB invoice with this DocNumber (or QB errored) — the job is
        // created fine, it just isn't linked. Surface it so the user can
        // create the invoice in QB and relink.
        return { ...result, qbInvoiceAdoptionFailed: true };
      } catch (error) {
        console.error("Failed to adopt QB invoice:", error);
        return { ...result, qbInvoiceAdoptionFailed: true };
      }
    }

    return result;
  } catch (error: any) {
    // Like updateJobV2: email the failure before rethrowing, so every
    // caller's failure reaches support with the submitted job attached.
    if (!String(error?.message || "").includes("already exists")) {
      authenticatedFetch("/api/email/errorNotification", {
        method: "POST",
        body: JSON.stringify({
          userName: user.settings.name,
          userId: user.uid,
          error: `createJobV2 failed: ${error?.message || error}
UserId: ${user.uid}
${JSON.stringify(job)}`,
          location: "createJobV2",
        }),
      }).catch(() => {});
    }
    throw error;
  }
}

export async function createMockJob(userId: string): Promise<JobV2> {
  try {
    const newJobRef = doc(db, `users/${userId}/jobs/mockJob`);
    const userMetaJobsDocRef = doc(db, `users/${userId}/metadata/jobs`);

    const userMetadataDoc = await getDoc(userMetaJobsDocRef);
    const newJobId = (userMetadataDoc.data()?.jobNum || 0) + 1;

    const newJob: JobV2 = {
      ...mockJobData,
      uid: newJobRef.id,
      jobId: newJobId,
    };

    // Updating the metadata document
    await updateDoc(userMetaJobsDocRef, { jobNum: newJobId });

    // Creating the mock job
    await setDoc(newJobRef, newJob);

    return newJob;
  } catch (error) {
    throw error;
  }
}

// When we have a payment it now might also mean we need to update the job line items too cause it could be a payment line item
// thus meaning we also need to update all prices etc

// Splits a form's file list into fresh picks to upload and stored records to
// keep. Only File objects upload. A stored record is kept as the form holds
// it: when the stored job has the same name the two are the same upload, and
// when it does not the record was written by another save (signed WRD,
// signed contract) that this form's copy of the job predates.
export function splitFilesForUpload<T extends { name: string }>(
  formFiles: (T | File)[],
  storedFiles: T[]
): [File[], T[]] {
  const toUpload: File[] = [];
  const toKeep: T[] = [];
  for (const file of formFiles) {
    if (file instanceof File) {
      toUpload.push(file);
      continue;
    }
    const stored = storedFiles.find((s) => s.name === file.name);
    toKeep.push(stored ?? file);
  }
  return [toUpload, toKeep];
}

export async function updateJobV2(
  user: User,
  currentJob: JobV2,
  updatedJobForm: JobV2,
  options?: {
    stages?: StageType[];
    // Split payments: the card is charged before the portion writes, so a
    // QuickBooks failure must not abort recording money that already moved.
    // The invoice self-heals on the job's next save; the failure is returned
    // in qbFailures for the caller to surface.
    qbBestEffort?: boolean;
  }
): Promise<{
  updatedJob: JobV2;
  updatedClientJob: ClientJobV2;
  partMoveFailures?: string[];
  qbFailures?: string[];
}> {
  const {
    uid: userId,
    details,
    settings: { qbBankAccount, qbIncomeAccount, qbTaxGroup },
  } = user;

  try {
    const jobDocRef = doc(db, `users/${userId}/jobs/${currentJob.uid}`);
    const clientDocRef = doc(
      db,
      `users/${userId}/clients/${updatedJobForm.client.uid}`
    );
    let clientData: Client;
    let stagedMaterial: Awaited<ReturnType<typeof stageMaterialDelta>> | null =
      null;

    // The form's payments are whatever it loaded with, and this function writes
    // the whole document — so a payment taken after the form opened would be
    // erased by an unrelated save. Fold any such payment back in.
    //
    // Deliberately a plain read, not transaction.get. Reading the job inside
    // the transaction would add it to the read set, so any concurrent write to
    // the job would retry the whole callback — and that callback is not safe to
    // re-run: the part location writes below (createPartLocation and friends)
    // are separate, non-transactional, and would fire twice.
    //
    // This runs before the QuickBooks preflight, which branches on payment
    // counts, and again immediately before the transaction to keep the window
    // between reading and writing as small as possible. It is idempotent.
    const reconcileAgainstLiveJob = async () => {
      const liveSnap = await getDoc(jobDocRef);
      const liveJob = liveSnap.exists() ? (liveSnap.data() as JobV2) : null;
      const reconciliation = reconcileStaleJobPayments(
        liveJob,
        currentJob,
        updatedJobForm
      );
      currentJob = reconciliation.currentJob;
      updatedJobForm = reconciliation.updatedJobForm;

      // Same rescue for files: a part photo added from a scanned tag or a
      // client's portal upload that landed after the form loaded would
      // otherwise be dropped from the line item by this write.
      const fileReconciliation = reconcileStaleJobFiles(
        liveJob,
        currentJob,
        updatedJobForm
      );
      currentJob = fileReconciliation.currentJob;
      updatedJobForm = fileReconciliation.updatedJobForm;
    };

    // The pre-reads are independent of each other. Stages can arrive
    // already-fetched from the caller's React Query cache, in which case
    // they cost nothing here.
    const [stagesData, clientSnap] = await Promise.all([
      options?.stages ?? getStages(userId),
      getDoc(clientDocRef),
      reconcileAgainstLiveJob(),
    ]);
    if (!clientSnap.exists()) {
      throw new Error("Client not found");
    }

    // Moving the job to another client. Checked after the payment reconcile
    // above so a payment that landed while the form was open still locks it.
    const isClientChange = currentJob.client.uid !== updatedJobForm.client.uid;
    const oldClientDocRef = isClientChange
      ? doc(db, `users/${userId}/clients/${currentJob.client.uid}`)
      : null;
    if (isClientChange) {
      const newClient = clientSnap.data() as Client;
      const blocked = jobClientSwitchBlockedReason(
        currentJob,
        newClient,
        updatedJobForm.syncedToAccountingIntegration
      );
      if (blocked) {
        throw new Error(blocked);
      }
      if (
        updatedJobForm.syncedToAccountingIntegration &&
        currentJob.syncedToAccountingIntegration &&
        !newClient.quickbooks?.id
      ) {
        throw new Error(
          `${newClient.fullName} has no QuickBooks customer yet, so the invoice can't be moved to them. Sync the client to QuickBooks first.`
        );
      }
    }

    // Refuse a full location before anything is written. The post-commit
    // part ops below can only warn once the job is already saved.
    await assertLocationsHaveRoom(
      userId,
      collectPartsEnteringLocations(
        currentJob.lineItems,
        updatedJobForm.lineItems
      )
    );

    // QuickBooks problems this save survived; returned to the caller.
    const qbFailures: string[] = [];

    let qbPreflightInvoiceId: string | null | undefined;
    try {
      qbPreflightInvoiceId = await preflightQuickBooksJobUpdateSync({
        user,
        currentJob,
        updatedJob: updatedJobForm,
        client: clientSnap.data() as Client,
      });
    } catch (error) {
      if (!options?.qbBestEffort) throw error;
      qbFailures.push(
        `QuickBooks invoice sync failed: ${
          (error as Error).message
        }. The invoice will correct itself the next time this job is saved.`
      );
    }

    if (qbPreflightInvoiceId) {
      updatedJobForm.qbInvoiceId = qbPreflightInvoiceId;
    }

    // Catch anything that landed during the QuickBooks preflight above, which
    // can take seconds. Last read before the write.
    await reconcileAgainstLiveJob();

    const updatedAtTime = Timestamp.now();

    // =-=-=-=-=-=-=-= Handle file updates =-=-=-=-=-=-=-=
    // Uploads happen before the transaction because the job doc embeds the
    // new URLs. Deletes wait until after the commit so a failed save never
    // removes a file the stored job still references; a failed save leaves
    // only orphaned uploads, which are harmless.
    // Only fresh picks (File objects) upload. A stored record the form holds
    // that currentJob lacks was uploaded elsewhere already (a WRD or contract
    // signed while this form was open) and is carried forward as it is. It
    // must never reach uploadJobFiles: a record is not a File, and the
    // "upload" writes the bytes of `undefined` over a signed document.
    const [jobFilesToUpload, jobFilesToKeep] = splitFilesForUpload(
      updatedJobForm.files,
      currentJob.files
    );

    // A record signed while the edit form was open (other device, job page
    // modal) is not in the form's stale file list. It must survive the save,
    // the same way currentJob.wrd does below.
    const signedRecordFiles = currentJob.wrd?.isSigned
      ? currentJob.files.filter(
          (file) =>
            isWrdFile(file.name) &&
            !updatedJobForm.files.some((f) => f.name === file.name)
        )
      : [];
    const jobFilesToDelete = currentJob.files.filter(
      (file) =>
        !updatedJobForm.files.some((f) => f.name === file.name) &&
        !signedRecordFiles.includes(file)
    );

    const newJobFiles =
      jobFilesToUpload.length > 0
        ? await uploadJobFiles(jobFilesToUpload, userId, currentJob.uid)
        : [];

    const updatedFiles = [
      ...jobFilesToKeep,
      ...newJobFiles,
      ...signedRecordFiles,
    ];

    const lineItemFilesByUid = new Map<string, JobFile[]>();
    const lineItemFilesToDelete: JobFile[] = [];

    await Promise.all(
      updatedJobForm.lineItems.map(async (lineItem) => {
        const currentLineItem = currentJob.lineItems.find(
          (item) => item.uid === lineItem.uid
        );
        const [filesToUpload, filesToKeep] = splitFilesForUpload(
          lineItem.files,
          currentLineItem?.files || []
        );

        lineItemFilesToDelete.push(
          ...(currentLineItem?.files.filter(
            (file) => !lineItem.files.some((f) => f.name === file.name)
          ) || [])
        );

        const newLineItemFiles =
          filesToUpload.length > 0
            ? await uploadLineItemFiles(
                filesToUpload,
                userId,
                currentJob.uid,
                lineItem.uid
              )
            : [];

        lineItemFilesByUid.set(lineItem.uid, [
          ...filesToKeep,
          ...newLineItemFiles,
        ]);
      })
    );

    // Non-transactional side effects (part locations, contract queues,
    // storage deletes) are collected during the transaction callback and
    // executed only after the commit succeeds. Inside the callback they
    // would run on every retry and stick even when the save ultimately
    // failed.
    let pendingPartOps: { message: string; run: () => Promise<unknown> }[] =
      [];
    let pendingContractQueueOps: (() => Promise<unknown>)[] = [];

    const result = await runTransaction(db, async (transaction) => {
      // Reads first: Firestore transactions reject reads after any write.
      const [clientDoc, oldClientDoc] = await Promise.all([
        transaction.get(clientDocRef),
        oldClientDocRef ? transaction.get(oldClientDocRef) : null,
      ]);
      const client = clientDoc.data() as Client;
      clientData = client;

      const partOps: typeof pendingPartOps = [];
      const contractQueueOps: typeof pendingContractQueueOps = [];

      let completedAtTime = currentJob.completedAt;

      // Location automation (advancing a stage when a part moves into a
      // location with an automation stage) deliberately does NOT run here.
      // It is scan-only (shiftPartLocation / bulkShiftParts); a manual edit
      // in the job form is the escape hatch and must save exactly what the
      // user chose.

      // =-=-=-=-=-=-=-= Handle completedAt update =-=-=-=-=-=-=-=
      const specimenLineItems = updatedJobForm.lineItems.filter((lineItem) =>
        isSpecimenLineItem(lineItem)
      );

      if (specimenLineItems.length > 0) {
        const currentSpecimenLineItems = currentJob.lineItems.filter(
          (lineItem) => isSpecimenLineItem(lineItem)
        );

        // Get lists of current and updated stages
        const { currentStages, updatedStages } = specimenLineItems.reduce(
          (acc, lineItem) => {
            const lineItemId = lineItem.uid;
            const currentStage = currentSpecimenLineItems.find(
              (item) => item.uid === lineItemId
            )?.stage;
            const updatedStage = lineItem.stage;
            return {
              currentStages: [...acc.currentStages, currentStage],
              updatedStages: [...acc.updatedStages, updatedStage],
            };
          },
          { currentStages: [], updatedStages: [] }
        );

        // Check if any stages have changed
        const hasStageChanges = currentStages.some(
          (stage, index) => stage !== updatedStages[index]
        );

        // Check if all updated stages are completed
        const allUpdatedStagesCompleted = updatedStages.every((stageName) => {
          const stage = stagesData.find((s) => s.name === stageName);
          return stage?.isCompletedStage || false;
        });

        // Check if all current stages were completed
        const allCurrentStagesCompleted = currentStages.every((stageName) => {
          const stage = stagesData.find((s) => s.name === stageName);
          return stage?.isCompletedStage || false;
        });

        // Handle manual tag changes first
        if (updatedJobForm.tags.includes("closed")) {
          updatedJobForm.tags = updatedJobForm.tags.filter(
            (tag) => tag !== "active"
          );
          // Only set completedAt if it's not already set
          if (!completedAtTime) {
            completedAtTime = updatedAtTime;
          }
        } else if (updatedJobForm.tags.includes("active")) {
          updatedJobForm.tags = updatedJobForm.tags.filter(
            (tag) => tag !== "closed"
          );
          // Don't modify completedAt time when manually marking as active
          // completedAtTime remains unchanged
        }

        // Then handle stage changes
        if (hasStageChanges) {
          if (allUpdatedStagesCompleted && !allCurrentStagesCompleted) {
            // Situation: All specimens became completed
            completedAtTime = updatedAtTime;
            updatedJobForm.tags = updatedJobForm.tags.filter(
              (tag) => tag !== "active"
            );
            updatedJobForm.tags.push("closed");
            updatedJobForm.tags = Array.from(new Set([...updatedJobForm.tags]));
          } else if (!allUpdatedStagesCompleted && allCurrentStagesCompleted) {
            // Situation: Specimens moved from completed to not completed
            completedAtTime = null;
            updatedJobForm.tags = updatedJobForm.tags.filter(
              (tag) => tag !== "closed"
            );
            updatedJobForm.tags.push("active");
            updatedJobForm.tags = Array.from(new Set([...updatedJobForm.tags]));
          }
        }
      }
      // Note: Removed forced "active" logic for service-only jobs to allow manual tag changes

      // =-=-=-=-=-=-=-= Handle job updates =-=-=-=-=-=-=-=

      const updatedJob: JobV2 = {
        uid: currentJob.uid,
        jobId: currentJob.jobId,
        client: {
          uid: client.uid,
          firstName: client.firstName,
          lastName: client.lastName,
          fullName: client.fullName,
          contactDetails: {
            phone: client.phone,
            email: client.email,
          },
          currency: client.currency,
          syncedToAccountingIntegration: client.syncedToAccountingIntegration,
          autoStageEmails: client.autoStageEmails ?? null,
          autoStageSMS: client.autoStageSMS ?? null,
        },
        shipping: updatedJobForm.shipping,
        priority: (Number(updatedJobForm.priority) as Priority) || null,
        tags: updatedJobForm.tags,
        notes: updatedJobForm.notes,
        invoiceNotes: updatedJobForm.invoiceNotes || "",
        trophyListNotes: updatedJobForm.trophyListNotes || "",
        ids: updatedJobForm.ids,
        files: updatedFiles,
        lineItems: updatedJobForm.lineItems.map((lineItem) => {
            const currentLineItem = currentJob.lineItems.find(
              (item) => item.uid === lineItem.uid
            );
            const updatedLineItemFiles =
              lineItemFilesByUid.get(lineItem.uid) ?? lineItem.files;

            const commonFields = {
              uid: lineItem.uid,
              tags: lineItem.tags,
              notes: lineItem.notes,
              files: updatedLineItemFiles,
              price: lineItem.price,
              chargePerSquareUnit: lineItem.chargePerSquareUnit || null,
              discountedPrice: lineItem.discountedPrice,
              depositAmount: lineItem.depositAmount,
              deposit: lineItem.deposit,
              depositType: lineItem.depositAmount ? lineItem.depositType : null,
              discountAmount: lineItem.discountAmount,
              discount: lineItem.discount,
              discountType: lineItem.discountAmount
                ? lineItem.discountType
                : null,
              // this handles new and existing line items
              createdAt: currentLineItem?.createdAt || updatedAtTime,
              updatedAt: currentLineItem?.updatedAt || updatedAtTime,
            };

            if (isSpecimenLineItem(lineItem)) {
              const specimenLineItem: JobSpecimenLineItem = {
                ...commonFields,
                frontFacingId: lineItem.frontFacingId,
                quantity: lineItem.quantity,
                stage: lineItem.stage,
                habitat: lineItem.habitat,
                ...((lineItem.clientUploadNote ||
                  (currentLineItem as JobSpecimenLineItem | undefined)
                    ?.clientUploadNote) && {
                  clientUploadNote:
                    lineItem.clientUploadNote ||
                    (currentLineItem as JobSpecimenLineItem | undefined)
                      ?.clientUploadNote,
                }),
                specimen: {
                  ...lineItem.specimen,
                  harvestDate: lineItem.specimen.harvestDate
                    ? lineItem.specimen.harvestDate instanceof Date
                      ? Timestamp.fromDate(
                          dayjs.utc(lineItem.specimen.harvestDate).toDate()
                        )
                      : lineItem.specimen.harvestDate
                    : null,
                  measurements: Object.values(
                    lineItem.specimen.measurements || {}
                  ).every((value) => value === null)
                    ? null
                    : Object.keys(lineItem.specimen.measurements!).reduce(
                        (acc, cur) => {
                          return {
                            ...acc,
                            [cur]: Number(lineItem.specimen.measurements![cur]),
                          };
                        },
                        {} as Record<string, number>
                      ),

                  // part when being updatedlocation is not getting the correct uid or jobid
                  parts: lineItem.specimen.parts.map((part) => ({
                    ...part,
                    jobUid: updatedJobForm.uid,
                    lineItemUid: lineItem.uid,
                    jobId: updatedJobForm.jobId,
                    species: lineItem.specimen.species,
                    frontFacingId: lineItem.frontFacingId,
                  })),
                },
                estimatedCompletedAt: lineItem.estimatedCompletedAt
                  ? lineItem.estimatedCompletedAt instanceof Date
                    ? Timestamp.fromDate(
                        dayjs.utc(lineItem.estimatedCompletedAt).toDate()
                      )
                    : lineItem.estimatedCompletedAt
                  : null,
                completedAt: (() => {
                  const stage = stagesData.find(
                    (s) => s.name === lineItem.stage
                  );
                  if (!stage?.isCompletedStage) return null;
                  // Stamp only on the transition into a completed stage; a
                  // later save of an already-completed item must not drift
                  // its completion date to the edit date.
                  return (
                    (currentLineItem as JobSpecimenLineItem | undefined)
                      ?.completedAt ?? updatedAtTime
                  );
                })(),
                ...(lineItem.materials?.length ? { materials: lineItem.materials } : {}),
              };

              return specimenLineItem;
            } else {
              const serviceLineItem: JobServiceLineItem = {
                ...commonFields,
                service: lineItem.service,
                linkedSpecimenUid: lineItem.linkedSpecimenUid || null,
                ticketNumber: lineItem.ticketNumber || null,
                ...(lineItem.materials?.length ? { materials: lineItem.materials } : {}),
              };
              return serviceLineItem;
            }
          }),
        payments: updatedJobForm.payments.map((payment) => ({
          ...payment,
          date:
            payment.date instanceof Date
              ? Timestamp.fromDate(dayjs.utc(payment.date).toDate())
              : payment.date,
        })),
        paymentStatus: updatedJobForm.paymentStatus,
        subtotal: updatedJobForm.subtotal,
        totalDiscount: updatedJobForm.totalDiscount,
        subtotalAfterDiscounts: updatedJobForm.subtotalAfterDiscounts,
        includeTax: updatedJobForm.includeTax,
        totalTax: updatedJobForm.totalTax,
        taxRate: updatedJobForm.taxRate,
        ...(updatedJobForm.qbInvoiceId && {
          qbInvoiceId: updatedJobForm.qbInvoiceId,
        }),
        // qbInvoiceLink isn't part of the edit form, so carry it over from the
        // stored job — the full-document set() below would otherwise drop it
        ...((updatedJobForm.qbInvoiceLink || currentJob.qbInvoiceLink) && {
          qbInvoiceLink:
            updatedJobForm.qbInvoiceLink || currentJob.qbInvoiceLink,
        }),
        grandTotal: updatedJobForm.grandTotal,
        totalDeposit: updatedJobForm.totalDeposit,
        remainingBalance: updatedJobForm.remainingBalance,
        syncedToAccountingIntegration:
          updatedJobForm.syncedToAccountingIntegration,
        staff: [],
        actions: [],
        staffQuery: [],
        actionsQuery: [],
        history: updatedJobForm.history,
        createdAt: updatedJobForm.createdAt,
        updatedAt: updatedAtTime,
        completedAt: completedAtTime,
        contract: updatedJobForm.contract || null,
        // A WRD signed while the edit form was open must not be reverted by
        // the form's stale copy.
        wrd: currentJob.wrd?.isSigned
          ? currentJob.wrd
          : updatedJobForm.wrd || null,
        ...(currentJob.communications && {
          communications: currentJob.communications,
        }),
        // Stamped so another tab's listener can tell this write from its own.
        lastWriteTabId: getTabId(),
      };

      // ==========================================
      // AUTO-UPDATE SOA STAGES ON FIRST PAYMENT
      // ==========================================
      // For SOA users, automatically update stages from "Awaiting Instruction" to "In Process"
      // when the first payment is captured - wrapped in try-catch to not block payment processing
      if (
        currentJob.payments.length === 0 &&
        updatedJob.payments.length > 0 &&
        userId === NamedUserUID.SOA
      ) {
        try {
          updatedJob.lineItems = updatedJob.lineItems.map((lineItem) => {
            if (
              isSpecimenLineItem(lineItem) &&
              lineItem.stage === SoaStages.AwaitingDeposit
            ) {
              return {
                ...lineItem,
                stage: SoaStages.InProcess,
              };
            }
            return lineItem;
          });
        } catch (error) {
          console.error(
            "Error auto-updating SOA stages on first payment:",
            error
          );
          // Send error notification but don't block the payment from being processed
          authenticatedFetch("/api/email/errorNotification", {
            method: "POST",
            body: JSON.stringify({
              userName: user.details.fullName,
              userId: userId,
              error: `Failed to auto-update SOA stages on first payment for job ${updatedJob.jobId}: ${error}`,
              location: "updateJobV2 - SOA stage auto-update",
            }),
          }).catch((emailError) => {
            console.error(
              "Failed to send error notification email:",
              emailError
            );
          });
        }
      }

      // handle parts updates (collected here, executed after the commit)
      updatedJob.lineItems.forEach((lineItem) => {
        if (isSpecimenLineItem(lineItem)) {
          const currentLineItem = currentJob.lineItems.find(
            (item) => isSpecimenLineItem(item) && item.uid === lineItem.uid
          ) as JobSpecimenLineItem | undefined;

          const currentParts = currentLineItem?.specimen.parts || [];
          const updatedParts = lineItem.specimen.parts;

          updatedParts.forEach((part) => {
            const currentPart = currentParts.find((cp) => cp.uid === part.uid);

            if (!currentPart) {
              part.updatedAt = updatedAtTime;
              part.createdAt = updatedAtTime;
              part.jobUid = updatedJobForm.uid;
              part.jobId = updatedJobForm.jobId;
              part.lineItemUid = lineItem.uid;
              part.frontFacingId = lineItem.frontFacingId;
              partOps.push({
                message: `${part.name || "A part"} was not added to ${
                  part.locationCode
                }.`,
                run: () => createPartLocation(userId, part),
              });
            } else if (currentPart.locationCode !== part.locationCode) {
              part.updatedAt = updatedAtTime;
              const previousLocationCode = currentPart.locationCode;
              partOps.push({
                message: `${part.name || "A part"} was not moved to ${
                  part.locationCode
                }.`,
                run: () =>
                  shiftPartLocation(
                    userId,
                    part.locationCode,
                    previousLocationCode,
                    part
                  ),
              });
            } else if (
              currentPart.name !== part.name ||
              currentPart.frontFacingId !== part.frontFacingId
            ) {
              part.updatedAt = updatedAtTime;
              partOps.push({
                message: `${part.name || "A part"} could not be updated in ${
                  part.locationCode
                }.`,
                run: () => updatePartLocation(userId, part),
              });
            }
          });

          // Handle part deletions
          currentParts.forEach((currentPart) => {
            const stillExists = updatedParts.some(
              (p) => p.uid === currentPart.uid
            );
            if (!stillExists) {
              partOps.push({
                message: `${
                  currentPart.name || "A part"
                } was not removed from ${currentPart.locationCode}.`,
                run: () => deletePartLocation(userId, currentPart),
              });
            }
          });
        }
      });

      // Clean up parts of any whole line item removed from the job. The loop
      // above only visits surviving line items, so a deleted mount's parts
      // would otherwise linger in the location cache (lineitem-missing).
      currentJob.lineItems.forEach((currentLineItem) => {
        if (!isSpecimenLineItem(currentLineItem)) return;
        const stillExists = updatedJob.lineItems.some(
          (item) => item.uid === currentLineItem.uid
        );
        if (stillExists) return;
        currentLineItem.specimen.parts.forEach((part) => {
          if (!part.locationCode) return;
          partOps.push({
            message: `${part.name || "A part"} was not removed from ${
              part.locationCode
            }.`,
            run: () => deletePartLocation(userId, part),
          });
        });
      });

      const updatedClientJob: ClientJobV2 = {
        uid: updatedJob.uid,
        jobId: updatedJob.jobId,
        lineItems: updatedJob.lineItems.map((lineItem) => {
          const commonFields: ClientJobBaseLineItem = {
            uid: lineItem.uid,
            discount: lineItem.discount,
            deposit: lineItem.deposit,
            price: lineItem.price,
            discountedPrice: lineItem.discountedPrice,
            createdAt: lineItem.createdAt as Timestamp,
            updatedAt: lineItem.updatedAt as Timestamp,
            files: lineItem.files as ClientFile[],
          };
          if (isSpecimenLineItem(lineItem)) {
            const specimenVersion: ClientJobSpecimenLineItem = {
              ...commonFields,
              quantity: lineItem.quantity,
              estimatedCompletedAt: lineItem.estimatedCompletedAt as Timestamp,
              completedAt: lineItem.completedAt as Timestamp,
              specimen: lineItem.specimen.species!,
              mountType: lineItem.specimen.mountType!,
              stage: lineItem.stage as Stage,
            };
            return specimenVersion;
          } else {
            const serviceVersion: ClientJobServiceLineItem = {
              ...commonFields,
              service: lineItem.service,
            };
            return serviceVersion;
          }
        }),
        paymentStatus: updatedJob.paymentStatus,
        totalDiscount: updatedJob.totalDiscount,
        grandTotal: updatedJob.grandTotal,
        totalDeposit: updatedJob.totalDeposit,
        remainingBalance: updatedJob.remainingBalance,
        createdAt: updatedJob.createdAt,
        updatedAt: updatedJob.updatedAt,
        completedAt: updatedJob.completedAt,
        ...(updatedJobForm.qbInvoiceId && {
          qbInvoiceId: updatedJobForm.qbInvoiceId,
        }),
      };

      const newChanges = getJobChanges(currentJob, updatedJob);

      const newHistoryEntry: HistoricalChange = {
        teamMember: details,
        changeTime: updatedAtTime,
        changes: newChanges,
      };

      // Don't record no-op saves as history entries
      const newHistory: HistoricalChange[] = newChanges.length
        ? [...(currentJob.history ?? []), newHistoryEntry]
        : currentJob.history ?? [];

      updatedJob.history = newHistory;

      // Update updatedAt for line items if they have changes
      // This is prone to one error cause if line items have the same identifier but not the same mount/service
      updatedJob.lineItems.forEach((lineItem) => {
        // Skip processing fee line items - they shouldn't need timestamp updates from history
        if (
          isServiceLineItem(lineItem) &&
          lineItem.service.includes("Processing Fee")
        ) {
          return;
        }

        const itemIdentifier = isSpecimenLineItem(lineItem)
          ? lineItem.specimen.species
          : lineItem.service;

        const hasChanges = newHistoryEntry.changes.some((change) =>
          change.field.includes(itemIdentifier)
        );

        if (hasChanges) {
          lineItem.updatedAt = updatedAtTime;
        }
      });

      if (currentJob.contract?.hasPaymentPlan) {
        if (updatedJob.contract?.hasPaymentPlan) {
          // Must of been an out of band payment
          if (currentJob.payments.length < updatedJob.payments.length) {
            const newPayment = updatedJob.payments.find(
              (updatedPayment) =>
                !currentJob.payments.some(
                  (currentPayment) =>
                    (currentPayment.date as Timestamp).toMillis() ===
                    (updatedPayment.date as Timestamp).toMillis()
                )
            );
            if (newPayment) {
              // Payments tagged with contractPaymentNumber are in-plan
              // (e.g. the initial payment from the collection flow) and are
              // already reflected in the schedule. Reconciling them here
              // would deduct the same amount from the remaining
              // installments a second time.
              if (newPayment.contractPaymentNumber == null) {
                const reconciliationAmount = newPayment.amount;
                const result = outOfBandPaymentReconciliation(
                  updatedJob,
                  reconciliationAmount
                );
                updatedJob.contract = result.contract;
                if (result.isFinished) {
                  contractQueueOps.push(() =>
                    moveContractToCompletedQueue(updatedJob.contract)
                  );
                } else {
                  contractQueueOps.push(() =>
                    updateContractInActiveQueue(updatedJob.contract)
                  );
                }
              } else {
                contractQueueOps.push(() =>
                  updateContractInActiveQueue(updatedJob.contract)
                );
              }
            } else {
              console.log("no new payment error");
              // idk lol should never get here
            }
          }

          const currentNumOfPayments = currentJob.contract.totalPayments;
          const currentMonthBetweenPayments =
            currentJob.contract.monthsBetweenPayments;
          const newNumOfPayments = updatedJob.contract.totalPayments;
          const newMonthBetweenPayments =
            updatedJob.contract.monthsBetweenPayments;
          const contractAmountsDifferent =
            currentJob.contract.paymentSchedule.some(
              (payment) =>
                payment.amount !==
                updatedJob.contract.paymentSchedule.find(
                  (p) => p.paymentNumber === payment.paymentNumber
                )?.amount
            );

          // Contract has been updated
          if (
            currentNumOfPayments !== newNumOfPayments ||
            currentMonthBetweenPayments !== newMonthBetweenPayments ||
            contractAmountsDifferent
          ) {
            contractQueueOps.push(() =>
              updateContractInActiveQueue(updatedJob.contract)
            );
          }
        } else {
          // contract has been removed
          contractQueueOps.push(() =>
            deleteContractFromActiveQueue(currentJob.contract?.uid)
          );
        }
      }

      const updateMaterialDelta = computeMaterialDelta(
        currentJob.lineItems,
        updatedJob.lineItems
      );
      if (updateMaterialDelta.size > 0) {
        stagedMaterial = await stageMaterialDelta(
          transaction,
          userId,
          updateMaterialDelta,
          userId,
          "Material changed",
          user.details,
          {
            jobId: updatedJob.jobId,
            jobUid: updatedJob.uid,
            // Mount IDs from both states so removed-material mounts still show.
            mountIds: mountIdsByInventoryItem([
              ...currentJob.lineItems,
              ...updatedJob.lineItems,
            ]),
          }
        );
      }

      // The jobs array is the largest payload in this transaction and most
      // saves (stage moves, notes, measurements) leave the portal-facing
      // entry identical, so only rewrite the client doc when it changed.
      const existingClientJob = (client.jobs ?? []).find(
        (job) => job.uid === currentJob.uid
      );
      if (!clientJobEntryUnchanged(existingClientJob, updatedClientJob)) {
        const filteredJobs = (client.jobs ?? []).filter(
          (job) => job.uid !== currentJob.uid
        );

        filteredJobs.push(updatedClientJob);
        transaction.set(clientDocRef, { jobs: filteredJobs }, { merge: true });
        clientData.jobs = filteredJobs;
      }

      // Client switch: the entry above landed on the new client, so take it
      // off the old one in the same commit.
      if (oldClientDoc?.exists()) {
        const oldClient = oldClientDoc.data() as Client;
        transaction.set(
          oldClientDocRef,
          {
            jobs: (oldClient.jobs ?? []).filter(
              (job) => job.uid !== currentJob.uid
            ),
          },
          { merge: true }
        );
      }

      transaction.set(jobDocRef, updatedJob);

      stagedMaterial?.apply();

      // Assigned last so a retried callback replaces, never appends.
      pendingPartOps = partOps;
      pendingContractQueueOps = contractQueueOps;

      return { updatedJob, updatedClientJob };
    });

    const updatedJob = result.updatedJob;
    const updatedClientJob = result.updatedClientJob;

    if (stagedMaterial) {
      await stagedMaterial.postCommit();
    }

    // =-=-=-=-=-=-=-= Post-commit side effects =-=-=-=-=-=-=-=
    // The job is saved; nothing below can void it, so nothing below may
    // throw. Part failures are returned for the caller to surface, the
    // rest are logged and emailed silently.
    const partMoveFailures: string[] = [];
    try {
      await Promise.all(
        pendingPartOps.map(async (op) => {
          try {
            await op.run();
          } catch {
            try {
              await op.run();
            } catch (error) {
              console.error("Part location update failed after save:", error);
              const reason = isLocationFullError(error)
                ? ` ${error.message}`
                : "";
              partMoveFailures.push(`${op.message}${reason}`);
            }
          }
        })
      );

      if (partMoveFailures.length > 0) {
        authenticatedFetch("/api/email/errorNotification", {
          method: "POST",
          body: JSON.stringify({
            userName: user.details.fullName,
            userId,
            error: `Part location updates failed after saving job ${
              updatedJob.jobId
            }: ${partMoveFailures.join(" | ")}`,
            location: "updateJobV2 - post-commit part ops",
          }),
        }).catch(() => {});
      }

      await Promise.all(
        pendingContractQueueOps.map(async (op) => {
          try {
            await op();
          } catch {
            try {
              await op();
            } catch (error) {
              console.error("Contract queue update failed after save:", error);
              authenticatedFetch("/api/email/errorNotification", {
                method: "POST",
                body: JSON.stringify({
                  userName: user.details.fullName,
                  userId,
                  error: `Contract queue update failed after saving job ${updatedJob.jobId}: ${error}`,
                  location: "updateJobV2 - post-commit contract queue",
                }),
              }).catch(() => {});
            }
          }
        })
      );

      const allFilesToDelete = [...jobFilesToDelete, ...lineItemFilesToDelete];
      if (allFilesToDelete.length > 0) {
        await deleteFiles(
          allFilesToDelete,
          `${userId}/jobs/${currentJob.uid}/files/`
        ).catch((error) => {
          // Orphaned storage files are harmless; never fail a saved job over them.
          console.error("Storage cleanup failed after save:", error);
        });
      }
    } catch (error) {
      console.error("Post-commit side effects failed:", error);
    }

    if (
      updatedJob.syncedToAccountingIntegration &&
      qbIncomeAccount &&
      qbBankAccount &&
      qbTaxGroup
    ) {
      const isNewDepositOrFullPayment =
        updatedJob.payments.length === 1 &&
        (updatedJob.payments[0].type === PaymentType.Deposit ||
          updatedJob.payments[0].type === PaymentType.Full) &&
        (currentJob.payments.length === 0 ||
          currentJob.payments[0].type !== updatedJob.payments[0].type ||
          currentJob.payments[0].amount !== updatedJob.payments[0].amount);

      if (
        !currentJob.syncedToAccountingIntegration &&
        userId === NamedUserUID.Mesquite &&
        updatedJob.payments.length > 0 &&
        updatedJob.qbInvoiceId
      ) {
        const firstPayment = updatedJob.payments[0];

        const payment: {
          jobId: string;
          client: Client;
          jobPayment: JobPayment;
          qbInvoiceId: string;
          qbBankAccount: QBBankAccount;
        } = {
          jobId: updatedJob.jobId.toString(),
          client: clientData,
          jobPayment: firstPayment,
          qbInvoiceId: updatedJob.qbInvoiceId,
          qbBankAccount: qbBankAccount,
        };

        try {
          const response = await authenticatedFetch(
            "/api/quickbooks/payments/create",
            {
              method: "POST",
              body: JSON.stringify(payment),
            }
          );

          if (response.ok) {
            const newQbPaymentResponse = await response.json();
            const qbPaymentId = newQbPaymentResponse.data.Id;
            const updatedPayments = [...updatedJob.payments];
            updatedPayments[0].qbPaymentId = qbPaymentId;
            updatedJob.payments = updatedPayments;

            await stampQbPaymentIdOnJobPayment(
              userId,
              updatedJob.uid,
              updatedPayments[0].uid,
              qbPaymentId
            );
          } else {
            // A non-ok response commits the payment with no qbPaymentId, so
            // a later refund or delete would skip QuickBooks entirely. Silent
            // until now; at minimum support has to hear about it.
            const failure = `QB payment create returned ${
              response.status
            } for job ${updatedJob.uid} (invoice ${updatedJob.qbInvoiceId})`;
            qbFailures.push(failure);
            authenticatedFetch("/api/email/errorNotification", {
              method: "POST",
              body: JSON.stringify({
                userName: user.settings.name,
                userId: userId,
                error: failure,
                location: `updateJobV2 - QB payment creation invoice ${updatedJob.qbInvoiceId}`,
              }),
            });
          }
        } catch (error) {
          qbFailures.push(
            `QB payment create failed for job ${updatedJob.uid}: ${
              (error as Error).message
            }`
          );
          authenticatedFetch("/api/email/errorNotification", {
            method: "POST",
            body: JSON.stringify({
              userName: user.settings.name,
              userId: userId,
              error: `Failed to create QB payment for ${updatedJob.uid}: ${error}`,
              location: `updateJobV2 - QB payment creation invoice ${updatedJob.qbInvoiceId}`,
            }),
          });
        }
      }

      if (
        currentJob.syncedToAccountingIntegration &&
        updatedJob.payments.length > currentJob.payments.length &&
        (!isNewDepositOrFullPayment || user.uid === NamedUserUID.Mesquite)
      ) {
        const newPayment = updatedJob.payments.find(
          (updatedPayment) =>
            !currentJob.payments.some(
              (currentPayment) =>
                (currentPayment.date as Timestamp).toMillis() ===
                (updatedPayment.date as Timestamp).toMillis()
            )
        );

        if (newPayment) {
          const payment: {
            jobId: string;
            client: Client;
            jobPayment: JobPayment;
            qbInvoiceId: string;
            qbBankAccount: QBBankAccount;
          } = {
            jobId: updatedJob.jobId.toString(),
            client: clientData,
            jobPayment: newPayment,
            qbInvoiceId: updatedJob.qbInvoiceId,
            qbBankAccount: qbBankAccount,
          };

          try {
            const response = await authenticatedFetch(
              "/api/quickbooks/payments/create",
              {
                method: "POST",
                body: JSON.stringify(payment),
              }
            );

            if (response.ok) {
              const newQbPaymentResponse = await response.json();
              const qbPaymentId = newQbPaymentResponse.data.Id;
              const updatedPayments = [...updatedJob.payments];
              const updatedPayment = updatedPayments.find(
                (payment) => payment.uid === newPayment?.uid
              );
              if (updatedPayment) {
                updatedPayment.qbPaymentId = qbPaymentId;
                await stampQbPaymentIdOnJobPayment(
                  userId,
                  updatedJob.uid,
                  updatedPayment.uid,
                  qbPaymentId
                );
              }
              updatedJob.payments = updatedPayments;
            } else {
              // Same silent-failure hole as the first-payment branch above.
              const failure = `QB payment create returned ${
                response.status
              } for job ${updatedJob.uid} (invoice ${updatedJob.qbInvoiceId})`;
              qbFailures.push(failure);
              authenticatedFetch("/api/email/errorNotification", {
                method: "POST",
                body: JSON.stringify({
                  userName: user.settings.name,
                  userId: userId,
                  error: failure,
                  location: `updateJobV2 - QB payment creation invoice ${updatedJob.qbInvoiceId}`,
                }),
              });
            }
          } catch (error) {
            qbFailures.push(
              `QB payment create failed for job ${updatedJob.uid}: ${
                (error as Error).message
              }`
            );
            authenticatedFetch("/api/email/errorNotification", {
              method: "POST",
              body: JSON.stringify({
                userName: user.settings.name,
                userId: userId,
                error: `Failed to create QB payment for ${updatedJob.uid}: ${error}`,
                location: `updateJobV2 - QB payment creation invoice ${updatedJob.qbInvoiceId}`,
              }),
            });
          }
        }
      }
    }

    return {
      ...result,
      partMoveFailures,
      ...(qbFailures.length ? { qbFailures } : {}),
    };
  } catch (error) {
    console.error("Error updating job:", error);
    throw error;
  }
}

export async function deleteJob(
  userId: string,
  job: JobV2,
  materialsToReturn?: Map<string, number>,
  teamMember?: User["details"]
) {
  try {
    const clientDocRef = doc(db, `users/${userId}/clients/${job.client.uid}`);
    const jobDocRef = doc(db, `users/${userId}/jobs/${job.uid}`);
    const jobMetadataDocRef = doc(db, `users/${userId}/metadata/jobs`);

    const fullDelta = computeMaterialDelta(job.lineItems, []);
    const materialDelta = materialsToReturn
      ? new Map(
          Array.from(fullDelta.entries())
            .filter(([uid]) => materialsToReturn.has(uid))
            .map(([uid]) => [uid, -(materialsToReturn.get(uid) || 0)])
        )
      : fullDelta;

    let stagedMaterial: Awaited<ReturnType<typeof stageMaterialDelta>> | null =
      null;

    await runTransaction(db, async (transaction) => {
      const clientSnap = await transaction.get(clientDocRef);
      const clientData = clientSnap.data() as Client;
      const jobMetadataSnap = await transaction.get(jobMetadataDocRef);
      const jobMetadataData = jobMetadataSnap.data();

      if (materialDelta.size > 0) {
        stagedMaterial = await stageMaterialDelta(
          transaction,
          userId,
          materialDelta,
          userId,
          `Job #${job.jobId} deleted — materials returned to stock`,
          teamMember,
          { jobId: job.jobId, jobUid: job.uid }
        );
      }

      const filteredJobs = clientData.jobs.filter(
        (clientJob) => clientJob.uid !== job.uid
      );
      const filteredComms = (clientData.communications || []).filter(
        (comm) => comm.jobUid !== job.uid
      );

      transaction.update(clientDocRef, {
        jobs: filteredJobs,
        communications: filteredComms,
      });
      transaction.delete(jobDocRef);

      if (job.jobId === jobMetadataData?.jobNum) {
        transaction.update(jobMetadataDocRef, {
          jobNum: jobMetadataData.jobNum - 1,
        });
      }

      stagedMaterial?.apply();
    });

    if (stagedMaterial) {
      await stagedMaterial.postCommit();
    }

    await removeJobPartsFromLocations(userId, [job]);

    // The job doc is already deleted at this point — a storage cleanup
    // failure must not surface as "delete failed". Orphaned files are
    // harmless (and admin impersonation can't pass the cross-tenant
    // storage rules at all).
    try {
      await deleteEntireFolder(userId, "jobs", job.uid);
    } catch (error) {
      console.error("Job deleted but storage cleanup failed:", error);
    }

    return job;
  } catch (error) {
    console.error("Error deleting job:", error);
    throw error;
  }
}

/** Deletes every job for a user. Jobs for the same client are deleted sequentially; clients run in parallel. Skips mockJob. */
export async function clearJobs(userId: string): Promise<number> {
  console.log(`[clearJobs] Starting for user ${userId}`);

  const [jobsSnapshot, clientsSnapshot] = await Promise.all([
    getDocs(collection(db, "users", userId, "jobs")),
    getDocs(collection(db, "users", userId, "clients")),
  ]);

  const jobs = jobsSnapshot.docs
    .map((d) => d.data() as JobV2)
    .filter((job) => job.uid !== "mockJob");

  const clientUids = new Set(
    clientsSnapshot.docs
      .map((d) => (d.data() as Client).uid)
      .filter((uid) => uid !== "mockClient")
  );

  const jobsByClient = new Map<string, JobV2[]>();
  const orphanJobs: JobV2[] = [];

  for (const job of jobs) {
    const clientUid = job.client?.uid;
    if (clientUid && clientUids.has(clientUid)) {
      const list = jobsByClient.get(clientUid) ?? [];
      list.push(job);
      jobsByClient.set(clientUid, list);
    } else {
      orphanJobs.push(job);
    }
  }

  const total = jobs.length;
  console.log(
    `[clearJobs] Found ${total} job(s) across ${jobsByClient.size} client(s)` +
      (orphanJobs.length > 0 ? ` (+${orphanJobs.length} without client)` : "")
  );

  let deleted = 0;
  let failed = 0;

  const deleteJobsSequentially = async (
    clientLabel: string,
    clientJobs: JobV2[]
  ) => {
    let clientDeleted = 0;
    let clientFailed = 0;

    for (let i = 0; i < clientJobs.length; i++) {
      const job = clientJobs[i];
      const label = `job #${job.jobId} (${job.uid})`;

      console.log(
        `[clearJobs] [${clientLabel}] (${i + 1}/${clientJobs.length}) Deleting ${label}...`
      );
      try {
        await deleteJob(userId, job);
        clientDeleted++;
        deleted++;
        console.log(
          `[clearJobs] [${clientLabel}] (${i + 1}/${clientJobs.length}) Deleted ${label}`
        );
      } catch (error) {
        clientFailed++;
        failed++;
        console.error(
          `[clearJobs] [${clientLabel}] (${i + 1}/${clientJobs.length}) Failed ${label}:`,
          error
        );
      }
    }

    console.log(
      `[clearJobs] [${clientLabel}] Done — ${clientDeleted}/${clientJobs.length} deleted` +
        (clientFailed > 0 ? `, ${clientFailed} failed` : "")
    );
  };

  await Promise.all([
    ...Array.from(jobsByClient.entries()).map(([clientUid, clientJobs]) =>
      deleteJobsSequentially(clientUid, clientJobs)
    ),
    ...(orphanJobs.length > 0
      ? [deleteJobsSequentially("no-client", orphanJobs)]
      : []),
  ]);

  console.log(
    `[clearJobs] Done. Deleted ${deleted}/${total}, failed ${failed}`
  );
  return deleted;
}

// Set once a fetched index turns out to carry no display totals (written
// before syncJobsIndex started adding them, i.e. before the backfill), so the
// dashboard stops paying for an index download it can't use this session.
let indexLacksDashboardTotals = false;

export async function getDashBoardStats(
  userId: string,
  startDate: Date,
  endDate: Date,
  timezone: string,
  queryClient: QueryClient
) {
  try {
    // Convert dates to first/last day of respective months in UTC
    const startInUtc = dayjs(startDate)
      .tz(timezone)
      .startOf("month")
      .utc()
      .toDate();
    const endInUtc = dayjs(endDate).tz(timezone).endOf("month").utc().toDate();

    const startTimestamp = Timestamp.fromDate(startInUtc);
    const endTimestamp = Timestamp.fromDate(endInUtc);

    let jobsList: JobV2[] = [];

    const hasCachedJobs = queryClient.getQueryData<JobV2[]>(["jobs", userId]);

    // Fast path: the slim jobs index (already cached when the board has been
    // opened) carries every field these stats read, including the
    // customer-facing totals. Used only when every job in range has those
    // totals; otherwise fall through to the full docs exactly as before.
    let indexEntries: JobBasic[] | null = null;
    let fetchedIndex = false;
    if (!hasCachedJobs) {
      // Only trust the cached index while the board's listener is keeping
      // it current; once that unmounts the cache stops updating.
      const cachedIndex = isJobsIndexLive(userId)
        ? queryClient.getQueryData<JobBasic[]>(jobsIndexQueryKey(userId))
        : undefined;
      try {
        if (cachedIndex) {
          indexEntries = cachedIndex;
        } else if (!indexLacksDashboardTotals) {
          indexEntries = await getJobsIndex(userId);
          fetchedIndex = true;
          if (
            indexEntries.length > 0 &&
            !indexEntries.some(
              (entry) => typeof entry.displayGrandTotal === "number"
            )
          ) {
            indexLacksDashboardTotals = true;
          }
        }
      } catch {
        indexEntries = null;
      }
    }
    const inRange = (createdAt: any) => {
      const millis =
        typeof createdAt?.toMillis === "function" ? createdAt.toMillis() : null;
      return (
        millis !== null &&
        millis >= startTimestamp.toMillis() &&
        millis <= endTimestamp.toMillis()
      );
    };
    const indexInRange = indexEntries?.filter(
      (entry) => entry.jobId !== 1 && inRange(entry.createdAt)
    );
    const indexIsComplete =
      !!indexInRange &&
      indexInRange.every(
        (entry) =>
          typeof entry.displayGrandTotal === "number" &&
          typeof entry.displayRemainingBalance === "number"
      );
    if (fetchedIndex && !indexIsComplete) {
      // Fetched but unusable for this range: don't pay for it again this
      // session, go straight to the full read next time.
      indexLacksDashboardTotals = true;
    }

    if (indexIsComplete) {
      // Structural subset of JobV2 for every field read below; the money
      // figures come from displayGrandTotal/displayRemainingBalance.
      jobsList = indexInRange as unknown as JobV2[];
    } else if (hasCachedJobs) {
      // Guard createdAt: the Firestore path below can only return docs that
      // have the field, but the cached array is every job, so one legacy doc
      // with a missing/malformed createdAt would throw and take the whole
      // dashboard down. Skip those instead, matching the query's behavior.
      jobsList = hasCachedJobs.filter((job) => {
        const createdAtMillis =
          typeof job.createdAt?.toMillis === "function"
            ? job.createdAt.toMillis()
            : null;
        if (createdAtMillis === null) return false;
        return (
          createdAtMillis >= startTimestamp.toMillis() &&
          createdAtMillis <= endTimestamp.toMillis() &&
          job.jobId !== 1
        );
      });
    } else {
      // Query jobs
      const jobsRef = collection(db, "users", userId, "jobs");
      const dateQuery = query(
        jobsRef,
        where("createdAt", ">=", startTimestamp),
        where("createdAt", "<=", endTimestamp),
        orderBy("createdAt", "asc")
      );

      const results = await getDocs(dateQuery);
      jobsList = results.docs.map((doc) => doc.data()) as JobV2[];
    }

    jobsList = jobsList.filter((job) => job.jobId !== 1);

    const activeJobs = jobsList.filter((job: JobV2) =>
      job.tags.includes("active")
    ).length;

    const stageBreakdown = jobsList.reduce((acc, job) => {
      job.lineItems.forEach((item) => {
        if (isSpecimenLineItem(item) && item.stage) {
          acc[item.stage] = (acc[item.stage] || 0) + item.quantity;
        }
      });
      return acc;
    }, {} as Record<string, number>);

    const monthlySales = jobsList.reduce((acc, job) => {
      const month = dayjs(job.createdAt.toDate())
        .tz(timezone)
        .format("MMM YYYY");

      // Defensive check incase no client in old data
      if (!job.client.currency) {
        console.log("No currency found for job", job.jobId);
        return acc;
      }

      const currencyCode = job.client.currency.code;

      // Initialize the currency object if it doesn't exist
      if (!acc[currencyCode]) {
        acc[currencyCode] = {};
      }

      // Initialize the month if it doesn't exist for this currency
      if (!acc[currencyCode][month]) {
        acc[currencyCode][month] = 0;
      }

      // Add the job's grand total using our math helper
      acc[currencyCode][month] = calculateAddition(
        acc[currencyCode][month],
        typeof (job as unknown as JobBasic).displayGrandTotal === "number"
          ? (job as unknown as JobBasic).displayGrandTotal
          : calculateDisplayGrandTotal(
              job.lineItems,
              job.subtotalAfterDiscounts,
              job.totalTax,
              job.grandTotal
            )
      );

      return acc;
    }, {} as Record<string, Record<string, number>>);

    const paymentStatusDistribution = jobsList.reduce((acc, job) => {
      const status = job.paymentStatus;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const outstandingBalance = jobsList.reduce((acc, job) => {
      // Defensive check for currency
      if (!job.client.currency) {
        console.log("No currency found for job", job.jobId);
        return acc;
      }

      const currencyCode = job.client.currency.code;

      // Initialize the currency total if it doesn't exist
      if (!acc[currencyCode]) {
        acc[currencyCode] = 0;
      }

      // Add the job's remaining balance using our math helper
      acc[currencyCode] = calculateAddition(
        acc[currencyCode],
        toNearZero(
          typeof (job as unknown as JobBasic).displayRemainingBalance ===
            "number"
            ? (job as unknown as JobBasic).displayRemainingBalance
            : calculateDisplayRemainingBalance(
                job.lineItems,
                job.subtotalAfterDiscounts,
                job.totalTax,
                job.grandTotal,
                job.payments,
                job.remainingBalance
              )
        )
      );

      return acc;
    }, {} as Record<string, number>);

    const now = dayjs().tz(timezone).startOf("day");
    const nextSevenDays = now.add(7, "days").endOf("day");

    // Names of the user's completed/final stages — specimens already in one of
    // these are finished, so they should not appear as an upcoming deadline.
    const dashboardStages = await getStages(userId);
    const completedStageNames = new Set(
      dashboardStages.filter((s) => s.isCompletedStage).map((s) => s.name)
    );

    const upcomingDeadlines = jobsList
      .flatMap((job) =>
        job.lineItems
          .filter((item) => {
            if (!isSpecimenLineItem(item) || !item.estimatedCompletedAt) {
              return false;
            }

            // Skip specimens already in a completed stage.
            if (completedStageNames.has(item.stage)) {
              return false;
            }

            const estimatedCompletedAtMillis = (
              item.estimatedCompletedAt as Timestamp
            ).toMillis();

            const startDate = now.startOf("day").toDate().getTime();
            const endDate = nextSevenDays.endOf("day").toDate().getTime();

            return (
              estimatedCompletedAtMillis >= startDate &&
              estimatedCompletedAtMillis <= endDate
            );
          })
          .map((item) => ({
            jobId: job.jobId,
            specimen: (item as JobSpecimenLineItem).specimen.species,
            deadline: (item as JobSpecimenLineItem).estimatedCompletedAt,
          }))
      )
      .sort((a, b) =>
        dayjs((a.deadline as Timestamp).toDate()).diff(
          dayjs((b.deadline as Timestamp).toDate())
        )
      );

    // 4. Job Type Distribution
    const mountTypeDistribution = jobsList.reduce((acc, job) => {
      job.lineItems.forEach((item) => {
        if (isSpecimenLineItem(item)) {
          const type = item.specimen.mountType;
          acc[type] = (acc[type] || 0) + item.quantity;
        }
      });
      return acc;
    }, {} as Record<string, number>);

    // 5. Job service type distribution
    const serviceTypeDistribution = jobsList.reduce((acc, job) => {
      job.lineItems.forEach((item) => {
        if (
          isServiceLineItem(item) &&
          !item.service.includes("Processing Fee")
        ) {
          const type = item.service;
          acc[type] = (acc[type] || 0) + 1;
        }
      });

      return acc;
    }, {} as Record<string, number>);

    return {
      activeJobs,
      paymentStatusDistribution,
      stageBreakdown,
      monthlySales,
      upcomingDeadlines,
      mountTypeDistribution,
      serviceTypeDistribution,
      outstandingBalance,
    };
  } catch (error) {
    console.error("Error getting dashboard stats:", error);
    throw error;
  }
}

export const countActiveJobs = async (userId: string): Promise<number> => {
  const jobsRef = collection(db, "users", userId, "jobs");
  const activeJobsQuery = query(
    jobsRef,
    where("tags", "array-contains", "active")
  );
  const snapshot = await getCountFromServer(activeJobsQuery);
  // Subtract 1 for the mock job
  return snapshot.data().count - 1;
};

export const bulkEditJobTags = async (
  userId: string,
  jobIds: string[],
  tags: string[],
  updateType: "add" | "delete"
) => {
  const jobsRef = collection(db, "users", userId, "jobs");
  const chunkSize = 30;
  const updatedJobs: JobV2[] = [];

  for (let i = 0; i < jobIds.length; i += chunkSize) {
    const chunk = jobIds.slice(i, i + chunkSize);
    if (!chunk.length) continue;

    const batch = writeBatch(db);
    const jobsQuery = query(jobsRef, where("uid", "in", chunk));
    const jobsSnapshot = await getDocs(jobsQuery);

    jobsSnapshot.forEach((job) => {
      const jobData = job.data() as JobV2;
      updatedJobs.push(jobData);
      const currentTags = jobData.tags;
      let uniqueTags = [...new Set([...currentTags, ...tags])];

      if (updateType === "add") {
        if (tags.includes("closed")) {
          uniqueTags = uniqueTags.filter((tag) => tag !== "active");
          batch.update(job.ref, { completedAt: Timestamp.now() });
        } else if (tags.includes("active")) {
          uniqueTags = uniqueTags.filter((tag) => tag !== "closed");
          batch.update(job.ref, { completedAt: null });
        }
      }
      if (updateType === "delete") {
        if (tags.includes("closed")) {
          uniqueTags.push("active");
          uniqueTags = uniqueTags.filter((tag) => tag !== "closed");
          batch.update(job.ref, { completedAt: null });
        } else if (tags.includes("active")) {
          uniqueTags.push("closed");
          uniqueTags = uniqueTags.filter((tag) => tag !== "active");
          batch.update(job.ref, { completedAt: Timestamp.now() });
        }
      }

      uniqueTags = [...new Set([...uniqueTags])];
      batch.update(job.ref, { tags: uniqueTags });
    });

    await batch.commit();
  }

  return updatedJobs;
};

export const bulkUpdateJobStages = async (
  userId: string,
  user: User,
  jobIds: string[],
  stageName: string,
  fromStage?: string
): Promise<{
  updatedJobs: JobV2[];
  totalMountsUpdated: number;
  stageUpdates: Array<{
    jobId: number;
    jobUid: string;
    clientUid: string;
    clientEmail?: string;
    clientPhone?: string;
    autoStageEmails: boolean | null;
    autoStageSMS: boolean | null;
    updates: Array<{ specimen: string; stage: string; oldStage: string }>;
  }>;
}> => {
  const batch = writeBatch(db);
  const stages = await getStages(userId);
  const updatedJobs: JobV2[] = [];
  let totalMountsUpdated = 0;
  const stageUpdates: Array<{
    jobId: number;
    jobUid: string;
    clientUid: string;
    clientEmail?: string;
    clientPhone?: string;
    autoStageEmails: boolean | null;
    autoStageSMS: boolean | null;
    updates: Array<{ specimen: string; stage: string; oldStage: string }>;
  }> = [];

  // Process in chunks of 10 to avoid "in" query limits
  const chunkSize = 10;
  for (let i = 0; i < jobIds.length; i += chunkSize) {
    const chunk = jobIds.slice(i, i + chunkSize);
    const jobsRef = collection(db, "users", userId, "jobs");
    const jobsQuery = query(jobsRef, where("uid", "in", chunk));
    const jobDocs = await getDocs(jobsQuery);

    for (const jobDoc of jobDocs.docs) {
      const job = jobDoc.data() as JobV2;
      const jobRef = jobDoc.ref;
      const updatedAtTime = Timestamp.now();

      // Track specimen updates for notifications
      const specimenUpdates: Array<{
        specimen: string;
        stage: string;
        oldStage: string;
      }> = [];

      // Update all specimen line items (optionally filtered by fromStage)
      const updatedLineItems = job.lineItems.map((lineItem) => {
        if (isSpecimenLineItem(lineItem)) {
          // Skip if fromStage filter is set and doesn't match
          if (fromStage && lineItem.stage !== fromStage) {
            return lineItem;
          }
          // Track the change if stage is different
          if (lineItem.stage !== stageName) {
            specimenUpdates.push({
              specimen: lineItem.specimen.species || "Unknown",
              stage: stageName,
              oldStage: lineItem.stage || "",
            });
          }

          // Update the stage and completedAt for the line item
          const stage = stages.find((s) => s.name === stageName);
          return {
            ...lineItem,
            stage: stageName,
            completedAt: stage?.isCompletedStage ? updatedAtTime : null,
            updatedAt: updatedAtTime,
          };
        }
        return lineItem;
      });

      // Check if all specimens are now in completed stages
      const specimenLineItems = updatedLineItems.filter(isSpecimenLineItem);
      const allSpecimensCompleted =
        specimenLineItems.length > 0 &&
        specimenLineItems.every((item) => {
          if (isSpecimenLineItem(item)) {
            const stage = stages.find((s) => s.name === item.stage);
            return stage?.isCompletedStage || false;
          }
          return true;
        });

      // Update job tags based on completion status
      let tags = [...job.tags];
      let completedAt = job.completedAt;

      if (allSpecimensCompleted) {
        tags = tags.filter((t) => t !== "active");
        if (!tags.includes("closed")) {
          tags.push("closed");
        }
        completedAt = completedAt || updatedAtTime;
      } else {
        tags = tags.filter((t) => t !== "closed");
        if (!tags.includes("active")) {
          tags.push("active");
        }
        completedAt = null;
      }

      // Create history entry
      const newHistoryEntry: HistoricalChange = {
        teamMember: user.details,
        changeTime: updatedAtTime,
        changes: specimenUpdates.map((update) => ({
          field: `${update.specimen} Stage`,
          before: update.oldStage,
          after: update.stage,
        })),
      };

      const updatedHistory = [...(job.history || []), newHistoryEntry];

      // Prepare the updated job
      const updatedJob: JobV2 = {
        ...job,
        lineItems: updatedLineItems,
        tags: [...new Set(tags)],
        completedAt,
        updatedAt: updatedAtTime,
        history: updatedHistory,
      };

      // Add to batch
      batch.update(jobRef, {
        lineItems: updatedLineItems,
        tags: [...new Set(tags)],
        completedAt,
        updatedAt: updatedAtTime,
        history: updatedHistory,
      });

      updatedJobs.push(updatedJob);

      totalMountsUpdated += specimenUpdates.length;

      // Only add to stageUpdates if there were actual changes
      if (specimenUpdates.length > 0) {
        // Check which stages have autoStageUpdates enabled
        const autoUpdateStages = stages.filter((s) => s.autoStageUpdates);
        const shouldSendNotification = autoUpdateStages.some(
          (s) => s.name === stageName
        );

        if (shouldSendNotification) {
          stageUpdates.push({
            jobId: job.jobId,
            jobUid: job.uid,
            clientUid: job.client.uid,
            clientEmail: job.client.contactDetails.email,
            clientPhone: job.client.contactDetails.phone,
            autoStageEmails: job.client.autoStageEmails,
            autoStageSMS: job.client.autoStageSMS,
            updates: specimenUpdates,
          });
        }
      }
    }
  }

  // Commit all updates
  await batch.commit();

  // The client page draws its stage pie from the job summaries on the client
  // doc, so they have to follow the jobs or the client's list shows the old
  // stages. Transaction per client so a concurrent client write isn't lost.
  // The jobs are already committed, so a failure here is logged, not thrown,
  // or the stage notifications would be skipped too.
  const updatedJobsByClient = new Map<string, JobV2[]>();
  updatedJobs.forEach((job) => {
    const clientJobs = updatedJobsByClient.get(job.client.uid) ?? [];
    clientJobs.push(job);
    updatedJobsByClient.set(job.client.uid, clientJobs);
  });

  const clientResults = await Promise.allSettled(
    Array.from(updatedJobsByClient.entries()).map(([clientUid, jobs]) =>
      runTransaction(db, async (transaction) => {
        const clientRef = doc(db, "users", userId, "clients", clientUid);
        const clientSnap = await transaction.get(clientRef);
        const clientJobs: ClientJobV2[] = clientSnap.data()?.jobs;
        if (!Array.isArray(clientJobs)) return;

        transaction.update(clientRef, {
          jobs: clientJobs.map((clientJob) => {
            const job = jobs.find((j) => j.uid === clientJob.uid);
            if (!job) return clientJob;
            return {
              ...clientJob,
              completedAt: job.completedAt,
              updatedAt: job.updatedAt,
              lineItems: clientJob.lineItems.map((item) => {
                const lineItem = job.lineItems.find((l) => l.uid === item.uid);
                if (!lineItem || !isSpecimenLineItem(lineItem)) return item;
                return {
                  ...item,
                  stage: lineItem.stage,
                  completedAt: lineItem.completedAt,
                  updatedAt: lineItem.updatedAt,
                };
              }),
            };
          }),
        });
      })
    )
  );
  clientResults.forEach((result) => {
    if (result.status === "rejected") {
      console.error("Bulk stage client sync failed:", result.reason);
    }
  });

  return { updatedJobs, totalMountsUpdated, stageUpdates };
};

export const getJobByJobUId = async (
  userId: string,
  jobUid: string
): Promise<JobV2> => {
  const jobsCollectionRef = doc(db, "users", userId, "jobs", jobUid);
  try {
    const docSnapshot = await getDoc(jobsCollectionRef);
    return docSnapshot.data() as JobV2;
  } catch (error) {
    throw error;
  }
};

export const getSpecimenLocations = async (userId: string, jobId?: number) => {
  try {
    // Get jobs - either all jobs or a specific job
    let jobs: JobV2[] = [];

    if (jobId) {
      const job = await getJobByJobId(userId, jobId);
      if (job) jobs = [job];
    } else {
      jobs = await getJobsV2(userId);
    }

    // Create a map of locations to specimens
    const locationMap: Record<string, string[]> = {};

    jobs.forEach((job) => {
      job.lineItems.forEach((lineItem) => {
        if (isSpecimenLineItem(lineItem)) {
          // Get the specimen location
          const location = lineItem.specimen.location || "Unknown";

          if (!locationMap[location]) {
            locationMap[location] = [];
          }

          // Add specimen info with job ID for reference
          const specimenInfo = `Job #${job.jobId}: ${
            lineItem.specimen.species || "Unknown Species"
          }`;

          // Only add if not already in the array
          if (!locationMap[location].includes(specimenInfo)) {
            locationMap[location].push(specimenInfo);
          }
        }
      });
    });

    console.log({ locationMap });
  } catch (error) {
    console.error("Error getting specimen locations:", error);
    throw error;
  }
};

export const createPresetJob = async (
  userId: string,
  job: JobV2,
  presetName: string,
  description: string
) => {
  const presetJobNameRef = doc(
    db,
    "users",
    userId,
    "metadata",
    "presetJobNames"
  );

  // Check if the document exists first
  const presetJobNameDoc = await getDoc(presetJobNameRef);

  if (!presetJobNameDoc.exists()) {
    // Create the document with the initial preset
    await setDoc(presetJobNameRef, {
      all: [{ name: presetName, description }],
    });
  } else {
    // Use arrayUnion to add the job name to the 'all' array
    await updateDoc(presetJobNameRef, {
      all: arrayUnion({ name: presetName, description }),
    });
  }

  const jobToSave = {
    ...job,
    lineItems: job.lineItems.map((lineItem) => {
      return {
        ...lineItem,
        files: [],
      };
    }),
    files: [],
  };

  const presetJobRef = doc(db, "users", userId, "presetJobs", presetName);
  await setDoc(presetJobRef, jobToSave);
};

export const getPresetJob = async (userId: string, presetName: string) => {
  const presetJobRef = doc(db, "users", userId, "presetJobs", presetName);
  const presetJob = await getDoc(presetJobRef);
  console.log({ presetJobdata: presetJob.data() });
  return presetJob.data() as JobV2;
};

export const updatePresetJob = async (
  userId: string,
  oldPresetName: string,
  newPresetName: string,
  job: JobV2,
  description: string
) => {
  const oldPresetJobRef = doc(db, "users", userId, "presetJobs", oldPresetName);
  const newPresetJobRef = doc(db, "users", userId, "presetJobs", newPresetName);
  const presetJobNameRef = doc(
    db,
    "users",
    userId,
    "metadata",
    "presetJobNames"
  );

  // Get current preset job names
  const presetJobNameDoc = await getDoc(presetJobNameRef);
  const currentPresetJobs = presetJobNameDoc.data()?.all || [];

  // Update the preset job names array by replacing the old entry
  const updatedPresetJobs = currentPresetJobs.map(
    (preset: { name: string; description: string }) =>
      preset.name === oldPresetName
        ? { name: newPresetName, description }
        : preset
  );

  // Use a batch to update both documents atomically
  const batch = writeBatch(db);

  // If the name changed, delete the old document and create the new one
  if (oldPresetName !== newPresetName) {
    batch.delete(oldPresetJobRef);
    batch.set(newPresetJobRef, job);
  } else {
    // Otherwise just update the existing document
    batch.set(oldPresetJobRef, job);
  }

  batch.set(presetJobNameRef, { all: updatedPresetJobs });

  await batch.commit();
};

export const deletePresetJob = async (userId: string, presetName: string) => {
  const presetJobRef = doc(db, "users", userId, "presetJobs", presetName);
  const presetJobNameRef = doc(
    db,
    "users",
    userId,
    "metadata",
    "presetJobNames"
  );

  // Get current preset job names
  const presetJobNameDoc = await getDoc(presetJobNameRef);
  const currentPresetJobs = presetJobNameDoc.data()?.all || [];

  // Filter out the preset job to be deleted
  const updatedPresetJobs = currentPresetJobs.filter(
    (preset: { name: string; description: string }) =>
      preset.name !== presetName
  );

  // Use a batch to delete the preset job and update the names list atomically
  const batch = writeBatch(db);
  batch.delete(presetJobRef);
  batch.set(presetJobNameRef, { all: updatedPresetJobs });

  await batch.commit();
};

export const getPresetJobNames = async (userId: string) => {
  const presetJobNameRef = doc(
    db,
    "users",
    userId,
    "metadata",
    "presetJobNames"
  );
  const presetJobNameData = await getDoc(presetJobNameRef);
  return presetJobNameData.data()?.all as {
    name: string;
    description: string;
  }[];
};

export async function saveSignedContract(
  userId: string,
  jobData: JobV2,
  contractBlob: Blob,
  signedMethod: "in-person" | "portal" = "in-person"
): Promise<{ downloadUrl: string; contractFile: JobFile }> {
  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import(
      "firebase/storage"
    );
    const storage = getStorage();

    // Upload to Firebase Storage
    const fileName = `${userId}/jobs/${jobData.uid}/files/contract/#${jobData.jobId}_contract.pdf`;
    const storageRef = ref(storage, fileName);

    const uploadResult = await uploadBytes(storageRef, contractBlob);
    const downloadUrl = await getDownloadURL(uploadResult.ref);

    // Create the file entry for the job files array
    const contractFile: JobFile = {
      name: `Contract_${jobData.jobId}.pdf`,
      url: downloadUrl,
      createdAt: Timestamp.now(),
    };

    // Update the job document with both contract info and file entry
    const jobRef = doc(db, `users/${userId}/jobs/${jobData.uid}`);

    // Get current job to preserve existing files
    const currentJobDoc = await getDoc(jobRef);
    const currentJob = currentJobDoc.data() as JobV2;

    // Filter out any existing contract files to avoid duplicates
    const existingFiles = (currentJob.files || []).filter(
      (file) => !file.name.startsWith("Contract_")
    );

    await updateDoc(jobRef, {
      "contract.contractUrl": downloadUrl,
      "contract.signedAt": Timestamp.now(),
      "contract.isSigned": true,
      "contract.signedMethod": signedMethod,
      files: [...existingFiles, contractFile],
      updatedAt: Timestamp.now(),
      lastWriteTabId: getTabId(),
    });

    console.log("downloadUrl", downloadUrl);
    return { downloadUrl, contractFile };
  } catch (error) {
    console.error("Error saving signed contract:", error);
    throw error;
  }
}

// Stores an in-person wildlife record on the job. The signature image only
// ever lives in the PDF. One record per job: collecting a new signature
// replaces the previous file.
export async function saveSignedWrd(
  userId: string,
  jobData: JobV2,
  wrdBlob: Blob,
  wrd: Omit<JobWrd, "isSigned" | "signedAt" | "signedMethod" | "fileUrl">,
  // Maine: seal numbers keyed by line item uid, written into the mount notes
  sealsByLineItem: Record<string, string> = {},
  // Taxidermy Designs: each animal's sex, also written into the mount notes
  sexesByLineItem: Record<string, string> = {}
): Promise<{
  wrd: JobWrd;
  wrdFile: JobFile;
  files: JobFile[];
  sealsByLineItem: Record<string, string>;
  sexesByLineItem: Record<string, string>;
}> {
  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import(
      "firebase/storage"
    );
    const storage = getStorage();
    const now = Timestamp.now();

    const storagePath = `${userId}/jobs/${jobData.uid}/files/wrd/WRD_SIGNED_${now.toMillis()}.pdf`;
    const uploadResult = await uploadBytes(ref(storage, storagePath), wrdBlob);
    const downloadUrl = await getDownloadURL(uploadResult.ref);

    const wrdFile: JobFile = {
      name: WRD_FILE_NAME,
      url: downloadUrl,
      createdAt: now,
    };

    const jobRef = doc(db, `users/${userId}/jobs/${jobData.uid}`);
    const currentJobDoc = await getDoc(jobRef);
    const currentJob = currentJobDoc.data() as JobV2;

    // One record per job: the previous signed copy is dropped and replaced.
    const currentFiles = (currentJob.files || []) as JobFile[];
    const replaced = currentFiles.filter((file) => isWrdFile(file.name));
    const files = [
      ...currentFiles.filter((file) => !isWrdFile(file.name)),
      wrdFile,
    ];

    const signedWrd: JobWrd = {
      ...wrd,
      required: true,
      isSigned: true,
      signedAt: now,
      signedMethod: "in-person",
      fileUrl: downloadUrl,
    };

    const hasNotes =
      Object.keys(sealsByLineItem).length > 0 ||
      Object.keys(sexesByLineItem).length > 0;
    const lineItems = hasNotes
      ? applyRecordNotesToLineItems(currentJob.lineItems || [], {
          sealsByLineItem,
          sexesByLineItem,
        })
      : undefined;

    await updateDoc(jobRef, {
      wrd: signedWrd,
      files,
      ...(hasNotes ? { lineItems } : {}),
      updatedAt: now,
      lastWriteTabId: getTabId(),
    });

    // Only once the job no longer points at it. Best effort: an orphaned
    // object is harmless, a dangling reference is not.
    if (replaced.length > 0) {
      deleteFiles(replaced, `${userId}/jobs/${jobData.uid}/files`).catch(
        (error) => console.warn("Could not delete replaced record:", error)
      );
    }

    return { wrd: signedWrd, wrdFile, files, sealsByLineItem, sexesByLineItem };
  } catch (error) {
    console.error("Error saving signed WRD:", error);
    throw error;
  }
}

export async function switchJobClient(
  userId: string,
  jobUid: string,
  newClientUid: string
): Promise<{ updatedJob: JobV2; oldClientUid: string }> {
  try {
    const result = await runTransaction(db, async (transaction) => {
      // Get job document
      const jobDocRef = doc(db, `users/${userId}/jobs/${jobUid}`);
      const jobDoc = await transaction.get(jobDocRef);

      if (!jobDoc.exists()) {
        throw new Error("Job not found");
      }

      const job = jobDoc.data() as JobV2;
      const oldClientUid = job.client.uid;

      // Skip if same client
      if (oldClientUid === newClientUid) {
        return { updatedJob: job, oldClientUid };
      }

      // Get both client documents
      const oldClientDocRef = doc(
        db,
        `users/${userId}/clients/${oldClientUid}`
      );
      const newClientDocRef = doc(
        db,
        `users/${userId}/clients/${newClientUid}`
      );

      const [oldClientDoc, newClientDoc] = await Promise.all([
        transaction.get(oldClientDocRef),
        transaction.get(newClientDocRef),
      ]);

      if (!oldClientDoc.exists() || !newClientDoc.exists()) {
        throw new Error("One or both clients not found");
      }

      const oldClient = oldClientDoc.data() as Client;
      const newClient = newClientDoc.data() as Client;

      // Create updated job with new client info
      const updatedAt = Timestamp.now();
      const updatedJob: JobV2 = {
        ...job,
        client: {
          uid: newClient.uid,
          firstName: newClient.firstName,
          lastName: newClient.lastName,
          fullName: newClient.fullName,
          contactDetails: {
            phone: newClient.phone,
            email: newClient.email,
          },
          currency: newClient.currency,
          syncedToAccountingIntegration:
            newClient.syncedToAccountingIntegration,
          autoStageEmails: newClient.autoStageEmails ?? null,
          autoStageSMS: newClient.autoStageSMS ?? null,
        },
        updatedAt,
      };

      // Create client job version
      const clientJob: ClientJobV2 = {
        uid: job.uid,
        jobId: job.jobId,
        lineItems: job.lineItems.map((lineItem) => {
          const commonFields: ClientJobBaseLineItem = {
            uid: lineItem.uid,
            discount: lineItem.discount,
            deposit: lineItem.deposit,
            price: lineItem.price,
            discountedPrice: lineItem.discountedPrice,
            createdAt: lineItem.createdAt as Timestamp,
            updatedAt: lineItem.updatedAt as Timestamp,
            files: lineItem.files as ClientFile[],
          };

          if (isSpecimenLineItem(lineItem)) {
            const specimenVersion: ClientJobSpecimenLineItem = {
              ...commonFields,
              quantity: lineItem.quantity,
              estimatedCompletedAt: lineItem.estimatedCompletedAt as Timestamp,
              completedAt: lineItem.completedAt as Timestamp,
              specimen: lineItem.specimen.species!,
              mountType: lineItem.specimen.mountType!,
              stage: lineItem.stage as Stage,
            };
            return specimenVersion;
          } else {
            const serviceVersion: ClientJobServiceLineItem = {
              ...commonFields,
              service: lineItem.service,
            };
            return serviceVersion;
          }
        }),
        paymentStatus: job.paymentStatus,
        totalDiscount: job.totalDiscount,
        grandTotal: job.grandTotal,
        totalDeposit: job.totalDeposit,
        remainingBalance: job.remainingBalance,
        createdAt: job.createdAt,
        updatedAt,
        completedAt: job.completedAt,
        ...(job.qbInvoiceId && { qbInvoiceId: job.qbInvoiceId }),
      };

      // Remove job from old client's jobs array
      const oldClientJobs = oldClient.jobs.filter(
        (clientJob) => clientJob.uid !== jobUid
      );

      // Add job to new client's jobs array
      const newClientJobs = [...(newClient.jobs || []), clientJob];

      // Update all documents in transaction
      transaction.update(oldClientDocRef, { jobs: oldClientJobs });
      transaction.update(newClientDocRef, { jobs: newClientJobs });
      transaction.set(jobDocRef, updatedJob);

      return { updatedJob, oldClientUid };
    });

    return result;
  } catch (error) {
    console.error("Error switching job client:", error);
    throw error;
  }
}

export const updatedJobDataAfterInitialPayment = async (
  job: JobV2,
  paymentResult: JobPaymentResult
) => {
  const latestPayment =
    paymentResult.updatedPayments[paymentResult.updatedPayments.length - 1];

  const updatedJob = {
    ...job,
    lineItems: [
      ...job.lineItems,
      ...(latestPayment.processingFee
        ? [paymentResult.newProcessingFeeLineItem]
        : []),
    ],
    payments: [...job.payments, latestPayment],
    paymentStatus: paymentResult.newPaymentStatus,
  };

  const currentValues = updatedJob;
  const lineItems = currentValues.lineItems;
  const payments = currentValues.payments || [];
  const includeTax = currentValues.includeTax;

  // Calculate effective tax rate - use 0 if tax is not included
  const effectiveTaxRate = includeTax ? currentValues.taxRate || 0 : 0;

  /**
   * Calculate all financial totals using our precision-safe math helpers
   * Each calculation returns a DecimalValue that maintains precision
   * We use .raw() to convert to number for form storage
   */
  const calculations = {
    // Basic totals
    subtotal: calculateJob.subtotal(lineItems).raw(),
    totalDiscount: calculateJob.totalDiscount(lineItems).raw(),
    subtotalAfterDiscounts: calculateJob
      .subtotalAfterDiscounts(lineItems)
      .raw(),

    // Tax calculations
    totalTax: calculateJob.tax(lineItems, effectiveTaxRate, includeTax).raw(),

    // Final totals
    grandTotal: calculateJob
      .grandTotal(lineItems, effectiveTaxRate, includeTax)
      .raw(),

    // Deposit and balance
    totalDeposit: calculateJob.totalDeposit(lineItems).raw(),
    remainingBalance: calculateJob
      .remainingBalance(lineItems, effectiveTaxRate, payments, includeTax)
      .raw(),
  };

  const paymentScheduleInitialPayment =
    updatedJob.contract?.paymentSchedule?.[0];

  // The initial payment is the one just appended, not necessarily
  // payments[0]; the job may already hold a booking deposit or similar.
  const initialPayment = payments[payments.length - 1];

  const updatedPaymentScheduleItem: PaymentScheduleItem = {
    ...paymentScheduleInitialPayment,
    status: "completed",
    dueDate: initialPayment.date,
    completedAt: initialPayment.date,
    ...(initialPayment.stripeTransactionId
      ? {
          stripeTransactionId: initialPayment.stripeTransactionId,
        }
      : {}),
  };

  // Tag the payment as belonging to this contract schedule item
  initialPayment.contractPaymentNumber = 0;

  updatedJob.contract.paymentSchedule[0] = updatedPaymentScheduleItem;
  updatedJob.contract.nextPaymentNumber = 1;

  /**
   * Update all form fields with calculated values
   * Using explicit setFieldValue calls to ensure type safety
   */

  const updatedJobCalcs: Partial<JobV2> = {
    subtotal: calculations.subtotal,
    totalDiscount: calculations.totalDiscount,
    subtotalAfterDiscounts: calculations.subtotalAfterDiscounts,
    totalTax: calculations.totalTax,
    grandTotal: calculations.grandTotal,
    totalDeposit: calculations.totalDeposit,
    remainingBalance: calculations.remainingBalance,
  };

  const finalJobForPersisting = {
    ...updatedJob,
    ...updatedJobCalcs,
  };

  return finalJobForPersisting;
};

export const deletePendingJob = async (
  userId: string,
  pendingJobUid: string
): Promise<void> => {
  await deleteDoc(doc(db, `users/${userId}/pendingJobs/${pendingJobUid}`));
};

export const pendingJobWithNewClient = async (
  userId: string,
  pendingJobUid: string,
  newClient: JobClient
) => {
  try {
    const pendingJobRef = doc(
      db,
      `users/${userId}/pendingJobs/${pendingJobUid}`
    );
    await updateDoc(pendingJobRef, {
      client: newClient,
      syncedToAccountingIntegration: newClient.syncedToAccountingIntegration,
    });
  } catch (error) {
    console.error("Error updating pending job with new client:", error);
    throw error;
  }
};

export const moveContractToCompletedQueue = async (contract: JobContract) => {
  try {
    const activeContractQueueRef = doc(
      db,
      `activeContractQueue/${contract.uid}`
    );
    const completedQueueRef = doc(db, `completedContracts/${contract.uid}`);

    await setDoc(completedQueueRef, contract);

    await deleteDoc(activeContractQueueRef);
  } catch (error) {
    console.error("Error moving contract to completed queue:", error);
    throw error;
  }
};

export const moveContractBackToActiveQueue = async (contractUid: string) => {
  try {
    const completedQueueRef = doc(db, `pausedContracts/${contractUid}`);
    const activeContractQueueRef = doc(
      db,
      `activeContractQueue/${contractUid}`
    );
    const completedContract = await getDoc(completedQueueRef);
    await setDoc(activeContractQueueRef, completedContract.data());

    await deleteDoc(completedQueueRef);
  } catch (error) {
    console.error("Error moving contract to completed queue:", error);
    throw error;
  }
};

export const updateContractInActiveQueue = async (contract: JobContract) => {
  try {
    const activeContractQueueRef = doc(
      db,
      `activeContractQueue/${contract.uid}`
    );
    await setDoc(activeContractQueueRef, contract);
  } catch (error) {
    console.error("Error updating contract in active queue:", error);
    throw error;
  }
};

export const deleteContractFromActiveQueue = async (contractUid: string) => {
  try {
    const activeContractQueueRef = doc(
      db,
      `activeContractQueue/${contractUid}`
    );
    await deleteDoc(activeContractQueueRef);
  } catch (error) {
    console.error("Error deleting contract from active queue:", error);
    throw error;
  }
};

// One-time fix: restore contract from history entry
// Grabs the "after" contract from a given history index and sets it on the job
export async function restoreContractFromHistory(
  userId: string,
  jobUid: string,
  historyIndex: number
) {
  const jobRef = doc(db, `users/${userId}/jobs/${jobUid}`);
  const jobDoc = await getDoc(jobRef);

  if (!jobDoc.exists()) throw new Error("Job not found");

  const job = jobDoc.data() as JobV2;
  const historyEntry = job.history?.[historyIndex];

  if (!historyEntry) throw new Error(`No history entry at index ${historyIndex}`);

  const contractChange = historyEntry.changes?.find(
    (c: any) => c.field === "contract"
  );

  if (!contractChange?.after) throw new Error("No contract change in that history entry");

  await updateDoc(jobRef, { contract: contractChange.after });

  return contractChange.after;
}
